import React, { useState, useEffect, useRef } from 'react';
import { Brain, TrendingUp, DollarSign, Calendar, Target, Users, ArrowRight, Check, AlertTriangle, Mail, Download, FileText, Zap, BarChart3, Sparkles, Eye, Lightbulb, Shield, Map, Cpu, Network, ChevronRight, ChevronDown, Info, TrendingDown, Clock, Award, Gauge, Globe, Search, Layers, Activity, PieChart, Radar, Navigation, ArrowUpRight, ArrowDownRight, Minus, Save, Image, Trash2, RefreshCw, Loader2, Sliders, X, Ban, Calculator } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Swords } from 'lucide-react'; 
import logger from '../utils/logger';

// Currency data for formatting
const CURRENCY_DATA = {
  'USD': { symbol: '$', name: 'US Dollar', position: 'before' },
  'EUR': { symbol: '€', name: 'Euro', position: 'before' },
  'GBP': { symbol: '£', name: 'British Pound', position: 'before' },
  'JPY': { symbol: '¥', name: 'Japanese Yen', position: 'before' },
  'CNY': { symbol: '¥', name: 'Chinese Yuan', position: 'before' },
  'INR': { symbol: '₹', name: 'Indian Rupee', position: 'before' },
  'AUD': { symbol: 'A$', name: 'Australian Dollar', position: 'before' },
  'CAD': { symbol: 'C$', name: 'Canadian Dollar', position: 'before' },
  'CHF': { symbol: 'CHF', name: 'Swiss Franc', position: 'before' },
  'KRW': { symbol: '₩', name: 'South Korean Won', position: 'before' },
  'SGD': { symbol: 'S$', name: 'Singapore Dollar', position: 'before' },
  'NZD': { symbol: 'NZ$', name: 'New Zealand Dollar', position: 'before' },
  'MXN': { symbol: 'MX$', name: 'Mexican Peso', position: 'before' },
  'BRL': { symbol: 'R$', name: 'Brazilian Real', position: 'before' },
  'ZAR': { symbol: 'R', name: 'South African Rand', position: 'before' },
  'AED': { symbol: 'د.إ', name: 'UAE Dirham', position: 'after' },
  'SAR': { symbol: 'ر.س', name: 'Saudi Riyal', position: 'after' },
  'NGN': { symbol: '₦', name: 'Nigerian Naira', position: 'before' },
  'KES': { symbol: 'KSh', name: 'Kenyan Shilling', position: 'before' },
  'GHS': { symbol: 'GH₵', name: 'Ghanaian Cedi', position: 'before' },
};

const PlatformIntelligence = () => {
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [businessInput, setBusinessInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [businessModel, setBusinessModel] = useState('');
  const [customBusinessModel, setCustomBusinessModel] = useState('');
  const [country, setCountry] = useState('United States');
  const [currency, setCurrency] = useState('USD');
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [customBudget, setCustomBudget] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [customPrimaryGoal, setCustomPrimaryGoal] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  
  // User preferences state
  const [platformsOwned, setPlatformsOwned] = useState([]);
  const [platformsToExclude, setPlatformsToExclude] = useState([]);
  
  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [showImplementationPlan, setShowImplementationPlan] = useState(null);
  const [showAIInsights, setShowAIInsights] = useState(null);
  const [error, setError] = useState(null);
  
  // New states for enhanced UI
  const [activeTab, setActiveTab] = useState('platforms');
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [showJourneyMap, setShowJourneyMap] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  
  // AI features state
  const [aiAnalysisStep, setAiAnalysisStep] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Countries and currencies data
  const [countriesData, setCountriesData] = useState([]);
  const [currenciesData, setCurrenciesData] = useState([]);

  // Saved analyses state
  const [showSavedAnalyses, setShowSavedAnalyses] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [savedSearchTerm, setSavedSearchTerm] = useState('');

  // Preferences modal state
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  // Map backend field names to frontend (add this after your state declarations)
// This ensures we can access data regardless of naming convention
const getRecommendationData = (recommendations) => {
  if (!recommendations) return {};
  return {
    // Map both possible field names
    customerJourney: recommendations.customerJourneyMap || recommendations.customerJourney,
    implementationRoadmap: recommendations.strategicRoadmap || recommendations.implementationRoadmap,
    marketIntelligence: recommendations.marketIntelligence,
    audiencePsychology: recommendations.audiencePsychology,
    riskAssessment: recommendations.riskAssessment,
    competitiveAnalysis: recommendations.competitiveAnalysis,
    platformTrends: recommendations.platformTrends,
    platformSynergies: recommendations.platformSynergies,
    performancePredictions: recommendations.performancePredictions,
    platformDiscovery: recommendations.platformDiscovery,
  };
};

  const navigate = useNavigate();
  const reportRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

  // Fetch countries and currencies on mount
  useEffect(() => {
    fetchCountriesAndCurrencies();
    fetchUserPreferences();
  }, []);

  // Update currency when country changes
  useEffect(() => {
    if (countriesData.length > 0) {
      const countryData = countriesData.find(c => c.name === country);
      if (countryData) {
        setCurrency(countryData.currency);
      }
    }
  }, [country, countriesData]);

  const fetchCountriesAndCurrencies = async () => {
    try {
      const [countriesResponse, currenciesResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/platform-intelligence/countries`),
        fetch(`${BACKEND_URL}/api/platform-intelligence/currencies`)
      ]);

      if (countriesResponse.ok) {
        const countriesResult = await countriesResponse.json();
        setCountriesData(countriesResult.data || []);
      }

      if (currenciesResponse.ok) {
        const currenciesResult = await currenciesResponse.json();
        setCurrenciesData(currenciesResult.data || []);
      }
    } catch (error) {
      logger.error('Error fetching countries/currencies:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPlatformsOwned(result.data.platforms_owned || []);
          setPlatformsToExclude(result.data.platforms_to_exclude || []);
        }
      }
    } catch (error) {
      logger.error('Error fetching preferences:', error);
    }
  };

  const saveUserPreferences = async () => {
    try {
      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          platforms_owned: platformsOwned,
          platforms_to_exclude: platformsToExclude
        })
      });

      if (response.ok) {
        setShowPreferencesModal(false);
      }
    } catch (error) {
      logger.error('Error saving preferences:', error);
    }
  };

  // Experience level options
  const experienceLevels = [
    { 
      id: 'beginner', 
      name: 'New to Paid Ads', 
      icon: '🌱',
      description: 'Never run ads or just getting started',
      features: [
        'Step-by-step setup guides',
        'Simpler platform recommendations',
        'Glossary tooltips enabled',
        'Conservative budget allocation'
      ]
    },
    { 
      id: 'intermediate', 
      name: 'Some Experience', 
      icon: '📈',
      description: '1-2 years running campaigns',
      features: [
        'Balanced recommendations',
        'Optimization tips included',
        'Industry benchmarks shown',
        'A/B testing suggestions'
      ]
    },
    { 
      id: 'advanced', 
      name: 'Seasoned Marketer', 
      icon: '🎯',
      description: '3+ years, manage significant budgets',
      features: [
        'Advanced platform mix options',
        'Programmatic/DSP recommendations',
        'Attribution modeling suggestions',
        'Raw data exports available'
      ]
    },
    { 
      id: 'agency', 
      name: 'Agency / Consultant', 
      icon: '🏢',
      description: 'Managing multiple client accounts',
      features: [
        'White-label report exports',
        'Client presentation mode',
        'Multi-account comparisons',
        'API access information'
      ]
    }
  ];

  // Fetch saved analyses
  const fetchSavedAnalyses = async () => {
    setIsLoadingSaved(true);
    setError(null);
    
    try {
      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      if (!token) {
        throw new Error('Please log in to view saved analyses');
      }

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/saved?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved analyses');
      }

      const result = await response.json();
      
      if (result.success) {
        setSavedAnalyses(result.data || []);
      } else {
        throw new Error(result.error || 'Failed to load analyses');
      }
    } catch (err) {
      logger.error('Error fetching saved analyses:', err);
      setError(err.message);
    } finally {
      setIsLoadingSaved(false);
    }
  };

  // Load saved analysis into state
  const handleLoadSavedAnalysis = (analysis) => {
    setBusinessInput(analysis.businessInput || '');
    setSelectedCategory(analysis.selectedCategory || '');
    setBusinessModel(analysis.businessModel || '');
    setCountry(analysis.country || 'United States');
    setCurrency(analysis.currency || 'USD');
    setMonthlyBudget(analysis.monthlyBudget || '');
    setCustomBudget(analysis.customBudget || '');
    setPrimaryGoal(analysis.primaryGoal || '');
    setTargetAudience(analysis.targetAudience || '');
    setRecommendations(analysis.recommendations);
    setCurrentStep(4);
    setActiveTab('overview');
    setShowSavedAnalyses(false);
  };

  // Delete saved analysis
  const handleDeleteSavedAnalysis = async (id, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this analysis?')) {
      return;
    }

    setDeletingId(id);
    
    try {
      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/saved/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete analysis');
      }

      setSavedAnalyses(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      logger.error('Error deleting analysis:', err);
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Open saved analyses modal
  const openSavedAnalyses = () => {
    setShowSavedAnalyses(true);
    fetchSavedAnalyses();
  };

  // Format date for display
  const formatSavedDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter saved analyses by search term
  const filteredSavedAnalyses = savedAnalyses.filter(analysis => {
    if (!savedSearchTerm) return true;
    const search = savedSearchTerm.toLowerCase();
    return (
      analysis.businessInput?.toLowerCase().includes(search) ||
      analysis.selectedCategory?.toLowerCase().includes(search) ||
      analysis.country?.toLowerCase().includes(search)
    );
  });

  // Helper function to get platform icon
  const getPlatformIcon = (platform) => {
    if (!platform) return '📱';
    
    const platformLower = platform.toLowerCase();
    
    if (platformLower.includes('meta') || platformLower.includes('facebook')) return '📘';
    if (platformLower.includes('instagram')) return '📷';
    if (platformLower.includes('google')) return '🔍';
    if (platformLower.includes('linkedin')) return '💼';
    if (platformLower.includes('tiktok')) return '🎵';
    if (platformLower.includes('youtube')) return '📺';
    if (platformLower.includes('twitter') || platformLower.includes(' x ')) return '🐦';
    if (platformLower.includes('pinterest')) return '📌';
    if (platformLower.includes('snapchat')) return '👻';
    if (platformLower.includes('reddit')) return '🤖';
    if (platformLower.includes('amazon')) return '📦';
    if (platformLower.includes('microsoft') || platformLower.includes('bing')) return '🪟';
    if (platformLower.includes('spotify')) return '🎧';
    if (platformLower.includes('trade desk')) return '📊';
    if (platformLower.includes('taboola') || platformLower.includes('outbrain')) return '📰';
    if (platformLower.includes('apple')) return '🍎';
    if (platformLower.includes('baidu')) return '🐉';
    if (platformLower.includes('yandex')) return '🇷🇺';
    if (platformLower.includes('naver')) return '🇰🇷';
    if (platformLower.includes('wechat')) return '💬';
    if (platformLower.includes('line')) return '💚';
    if (platformLower.includes('telegram')) return '✈️';
    if (platformLower.includes('whatsapp')) return '💬';
    if (platformLower.includes('vk') || platformLower.includes('vkontakte')) return '🇷🇺';
    if (platformLower.includes('programmatic') || platformLower.includes('dsp')) return '🤖';
    if (platformLower.includes('native')) return '📝';
    if (platformLower.includes('video') || platformLower.includes('ctv')) return '📺';
    if (platformLower.includes('audio') || platformLower.includes('podcast')) return '🎙️';
    if (platformLower.includes('display')) return '🖼️';
    
    return '📱';
  };

        // WhyNotSection Component - Attention-grabbing version
        const WhyNotSection = ({ excludedPlatforms }) => {
          const [expanded, setExpanded] = useState(false);
          
          if (!excludedPlatforms || excludedPlatforms.length === 0) return null;
          
          return (
            <div className={`relative overflow-hidden rounded-xl transition-all ${
              expanded 
                ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30' 
                : 'bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/20 hover:border-amber-500/40'
            }`}>
              {/* Subtle animated glow effect when collapsed */}
              {!expanded && (
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 animate-pulse" />
              )}
              
              <button
                onClick={() => setExpanded(!expanded)}
                className="relative w-full flex items-center justify-between p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <span className="text-2xl">🤔</span>
                    {!expanded && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full animate-ping" />
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-amber-300">
                      Why didn't we recommend {excludedPlatforms.length} other platforms?
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {expanded ? 'Click to collapse' : '👆 Tap to discover hidden insights'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full font-semibold">
                    {excludedPlatforms.length} analyzed
                  </span>
                  <ChevronDown className={`w-5 h-5 text-amber-400 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {expanded && (
                <div className="px-4 pb-4 space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-200">
                      We analyzed these platforms but they weren't the best fit for your specific business, budget, and goals. 
                      Here's why each one didn't make the cut:
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {excludedPlatforms.map((platform, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-amber-500/30 transition-all group"
                      >
                        <div className="text-xl opacity-50 group-hover:opacity-100 transition-opacity">
                          {getPlatformIcon(platform.platform_name)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-300">{platform.platform_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              (platform.fit_score || 0) < 30 
                                ? 'bg-red-500/20 text-red-400' 
                                : (platform.fit_score || 0) < 60 
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {platform.fit_score || 0}% fit
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{platform.exclusion_reason}</p>
                          {platform.would_recommend_at_level && (
                            <div className="mt-2 p-2 bg-amber-500/10 rounded border border-amber-500/20">
                              <p className="text-xs text-amber-400 flex items-center gap-1">
                                <span>💡</span> 
                                <span className="font-medium">When to reconsider:</span>
                              </p>
                              <p className="text-xs text-amber-300 mt-1">{platform.would_recommend_at_level}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        };

  // Budget Simulator Component
  const BudgetSimulator = ({ 
    currentBudget, 
    currency: simCurrency, 
    platforms, 
    onSimulate,
    budgetScenarios 
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [sliderValue, setSliderValue] = useState(currentBudget);
    const [isSimulating, setIsSimulating] = useState(false);
    
    const budgetMultipliers = [0.5, 0.75, 1, 1.5, 2, 3, 5];
    const presetBudgets = budgetMultipliers.map(m => Math.round(currentBudget * m));
    
    const formatBudgetLocal = (amount) => {
      const currencyInfo = CURRENCY_DATA[simCurrency] || { symbol: '$', position: 'before' };
      const formatted = amount.toLocaleString();
      return currencyInfo.position === 'before' 
        ? `${currencyInfo.symbol}${formatted}`
        : `${formatted} ${currencyInfo.symbol}`;
    };
    
    const handleSimulate = async (budget) => {
      setIsSimulating(true);
      if (onSimulate) {
        await onSimulate(budget);
      }
      setIsSimulating(false);
    };
    
    return (
      <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-semibold text-white">Budget Simulator</span>
            <span className="text-xs text-gray-400">— What if my budget was different?</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="mt-4 space-y-4">
            {/* Budget slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Adjust budget:</span>
                <span className="text-lg font-bold text-white">{formatBudgetLocal(sliderValue)}/mo</span>
              </div>
              <input
                type="range"
                min={presetBudgets[0]}
                max={presetBudgets[presetBudgets.length - 1]}
                step={100}
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatBudgetLocal(presetBudgets[0])}</span>
                <span>{formatBudgetLocal(presetBudgets[presetBudgets.length - 1])}</span>
              </div>
            </div>
            
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2">
              {budgetMultipliers.map((multiplier, idx) => {
                const budget = presetBudgets[idx];
                const isActive = sliderValue === budget;
                const isCurrent = multiplier === 1;
                
                return (
                  <button
                    key={multiplier}
                    onClick={() => setSliderValue(budget)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                      isActive 
                        ? 'bg-purple-500 text-white' 
                        : isCurrent
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {isCurrent ? 'Current' : `${multiplier}x`}
                  </button>
                );
              })}
            </div>
            
            {/* Simulate button */}
            <button
              onClick={() => handleSimulate(sliderValue)}
              disabled={isSimulating || sliderValue === currentBudget}
              className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                sliderValue === currentBudget
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
              }`}
            >
              {isSimulating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Recalculating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Simulate with {formatBudgetLocal(sliderValue)}
                </>
              )}
            </button>
            
            {/* Show what changes at different budgets */}
            {budgetScenarios && budgetScenarios.thresholds && (
              <div className="mt-4 p-3 bg-gray-800/50 rounded-lg">
                <h4 className="text-xs font-semibold text-gray-300 mb-2">Budget Thresholds:</h4>
                <div className="space-y-2 text-xs">
                  {budgetScenarios.thresholds.map((threshold, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        currentBudget >= threshold.amount ? 'bg-emerald-500' : 'bg-gray-600'
                      }`} />
                      <span className="text-gray-400">{threshold.amount_display || formatBudgetLocal(threshold.amount)}:</span>
                      <span className="text-gray-300">{threshold.unlock}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

 // Seasonality Insights Component
const SeasonalityInsights = ({ seasonalityData, countryName, category }) => {
  if (!seasonalityData || Object.keys(seasonalityData).length === 0) return null;
  
  const currentMonth = new Date().getMonth() + 1;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Handle both old format (monthly) and new format (monthly_factors)
  const monthlyData = seasonalityData.monthly_factors || seasonalityData.monthly || {};
  
  const nextThreeMonths = [0, 1, 2].map(offset => {
    const month = ((currentMonth - 1 + offset) % 12) + 1;
    const data = monthlyData[month] || monthlyData[month.toString()] || null;
    return {
      monthIndex: (currentMonth - 1 + offset) % 12,
      data: data
    };
  }).filter(item => item.data);
  
  // Get upcoming high-impact holidays
  const upcomingHolidays = Array.isArray(seasonalityData.holidays) 
    ? seasonalityData.holidays
        .filter(h => h.impact === 'high' || h.impact === 'very_high' || h.impact === 'extreme')
        .slice(0, 3)
    : [];
  
  if (nextThreeMonths.length === 0) return null;
  
  return (
    <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-5 h-5 text-orange-500" />
        <span className="text-sm font-semibold text-white">Seasonal Insights for {countryName}</span>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        {nextThreeMonths.map((item, idx) => {
          const monthData = item.data;
          const isCurrentMonth = idx === 0;
          
          return (
            <div 
              key={idx}
              className={`p-3 rounded-lg ${
                isCurrentMonth 
                  ? 'bg-orange-500/20 border border-orange-500/30' 
                  : 'bg-gray-800/50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-300">
                  {monthData.month_name || monthNames[item.monthIndex]}
                  {isCurrentMonth && <span className="text-orange-400 ml-1">(Now)</span>}
                </span>
                {monthData.budget_multiplier && monthData.budget_multiplier !== 1 && (
                  <span className={`text-xs font-bold ${
                    monthData.budget_multiplier > 1 ? 'text-emerald-400' : 'text-yellow-400'
                  }`}>
                    {monthData.budget_multiplier > 1 ? '↑' : '↓'} 
                    {Math.round((monthData.budget_multiplier - 1) * 100)}%
                  </span>
                )}
              </div>
              
              {/* Competition level */}
              {monthData.competition_level && (
                <p className={`text-xs mb-1 ${
                  monthData.competition_level === 'low' ? 'text-emerald-400' :
                  monthData.competition_level === 'medium' ? 'text-yellow-400' :
                  monthData.competition_level === 'high' ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {monthData.competition_level.replace('_', ' ')} competition
                </p>
              )}
              
              {/* Local events */}
              {monthData.local_events && monthData.local_events.length > 0 && (
                <p className="text-xs text-white font-medium">
                  {monthData.local_events.slice(0, 2).join(', ')}
                </p>
              )}
              
              {/* Tip or opportunity */}
              {monthData.tip && (
                <p className="text-xs text-gray-400 mt-1">{monthData.tip}</p>
              )}
              {!monthData.tip && monthData.opportunity && (
                <p className="text-xs text-gray-400 mt-1">{monthData.opportunity}</p>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Upcoming holidays */}
      {upcomingHolidays.length > 0 && (
        <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
          <p className="text-xs font-semibold text-gray-300 mb-2">🎯 Key Upcoming Events</p>
          <div className="space-y-1">
            {upcomingHolidays.map((holiday, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-white">{holiday.name}</span>
                {holiday.ad_spend_multiplier && holiday.ad_spend_multiplier > 1 && (
                  <span className="text-emerald-400">
                    +{Math.round((holiday.ad_spend_multiplier - 1) * 100)}% spend
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Current month recommendation */}
      {seasonalityData.current_month_recommendation && (
        <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <p className="text-xs text-orange-300">
            <span className="font-semibold">💡 This Month:</span> {seasonalityData.current_month_recommendation}
          </p>
        </div>
      )}
      
      {/* Annual peak - handles both old and new format */}
      {(seasonalityData.annual_peak_season || seasonalityData.annual_peak) && (
        <div className="mt-3 p-2 bg-gray-800/50 rounded-lg">
          <p className="text-xs text-gray-300">
            <span className="text-yellow-400">📈 Annual Peak:</span> {seasonalityData.annual_peak_season || seasonalityData.annual_peak}
          </p>
        </div>
      )}
      
      {/* Budget timing advice */}
      {seasonalityData.budget_timing_advice && (
        <div className="mt-2 p-2 bg-gray-800/50 rounded-lg">
          <p className="text-xs text-gray-300">
            <span className="text-emerald-400">💰 Budget Tip:</span> {seasonalityData.budget_timing_advice}
          </p>
        </div>
      )}
    </div>
  );
};

  // Helper to get UI complexity based on experience
  const getUIComplexity = (expLevel) => {
    switch (expLevel) {
      case 'beginner':
        return {
          showAdvancedMetrics: false,
          showProgrammaticPlatforms: false,
          showAttributionModels: false,
          simplifyTerms: true,
          maxPlatforms: 2,
          showGlossary: true
        };
      case 'intermediate':
        return {
          showAdvancedMetrics: true,
          showProgrammaticPlatforms: false,
          showAttributionModels: false,
          simplifyTerms: false,
          maxPlatforms: 3,
          showGlossary: false
        };
      case 'advanced':
      case 'agency':
        return {
          showAdvancedMetrics: true,
          showProgrammaticPlatforms: true,
          showAttributionModels: true,
          simplifyTerms: false,
          maxPlatforms: 5,
          showGlossary: false
        };
      default:
        return {
          showAdvancedMetrics: true,
          showProgrammaticPlatforms: false,
          showAttributionModels: false,
          simplifyTerms: false,
          maxPlatforms: 3,
          showGlossary: false
        };
    }
  };

  // Glossary tooltip component for beginners
  const GlossaryTooltip = ({ term, children }) => {
    const glossary = {
      'CPC': 'Cost Per Click - What you pay each time someone clicks your ad',
      'CPM': 'Cost Per Mille - What you pay per 1,000 ad impressions',
      'ROAS': 'Return On Ad Spend - Revenue earned for every dollar spent on ads',
      'CTR': 'Click-Through Rate - Percentage of people who click after seeing your ad',
      'CPA': 'Cost Per Acquisition - What you pay to acquire one customer',
      'Retargeting': 'Showing ads to people who already visited your website',
      'Lookalike Audience': 'People similar to your existing customers',
      'Impression': 'One view of your ad by someone'
    };
    
    const definition = glossary[term];
    if (!definition) return children;
    
    return (
      <span className="relative group cursor-help">
        {children}
        <span className="underline decoration-dotted decoration-gray-500">{term}</span>
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-xs text-gray-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 border border-gray-700">
          {definition}
        </span>
      </span>
    );
  };

  // Business categories
  const businessCategories = [
    { id: 'saas', name: 'SaaS & Technology', icon: '💻', description: 'Software, apps, and tech solutions' },
    { id: 'ecommerce', name: 'E-commerce & Retail', icon: '🛒', description: 'Online stores and retail' },
    { id: 'health', name: 'Health & Wellness', icon: '🏥', description: 'Healthcare and wellness services' },
    { id: 'finance', name: 'Financial Services', icon: '💰', description: 'Banking, fintech, insurance' },
    { id: 'education', name: 'Education & Training', icon: '🎓', description: 'Learning and education' },
    { id: 'realestate', name: 'Real Estate', icon: '🏠', description: 'Property and real estate' },
    { id: 'food', name: 'Food & Beverage', icon: '🍕', description: 'Restaurants and food services' },
    { id: 'travel', name: 'Travel & Hospitality', icon: '✈️', description: 'Travel and hospitality' },
    { id: 'professional', name: 'Professional Services', icon: '👔', description: 'B2B and consulting' },
    { id: 'entertainment', name: 'Entertainment & Media', icon: '🎬', description: 'Gaming, media, events' },
    { id: 'automotive', name: 'Automotive', icon: '🚗', description: 'Cars and automotive services' },
    { id: 'beauty', name: 'Beauty & Fashion', icon: '💄', description: 'Cosmetics and fashion' },
    { id: 'custom', name: 'Other / Custom', icon: '✨', description: 'Describe your own category' }
  ];

  // Business models
  const businessModels = [
    { id: 'b2b', name: 'B2B (Business to Business)', description: 'Selling to other businesses' },
    { id: 'b2c', name: 'B2C (Business to Consumer)', description: 'Selling directly to consumers' },
    { id: 'marketplace', name: 'Marketplace', description: 'Connecting buyers and sellers' },
    { id: 'subscription', name: 'Subscription', description: 'Recurring revenue model' },
    { id: 'ecommerce', name: 'E-commerce', description: 'Online product sales' },
    { id: 'freemium', name: 'Freemium', description: 'Free with premium upgrades' },
    { id: 'custom', name: 'Other / Custom', description: 'Describe your own model' }
  ];

  // Primary goals
  const primaryGoals = [
    { id: 'awareness', name: 'Brand Awareness', icon: '👁️', description: 'Increase brand recognition' },
    { id: 'leads', name: 'Lead Generation', icon: '🎯', description: 'Generate qualified leads' },
    { id: 'sales', name: 'Direct Sales', icon: '💳', description: 'Drive immediate purchases' },
    { id: 'traffic', name: 'Website Traffic', icon: '🌐', description: 'Increase website visitors' },
    { id: 'engagement', name: 'Engagement', icon: '❤️', description: 'Build community and engagement' },
    { id: 'installs', name: 'App Installs', icon: '📱', description: 'Drive app downloads' },
    { id: 'custom', name: 'Other / Custom', icon: '✨', description: 'Describe your own goal' }
  ];

  // Get currency symbol
  const getCurrencySymbol = () => {
    const currencyInfo = currenciesData.find(c => c.code === currency);
    return currencyInfo?.symbol || '$';
  };

  // Format currency display
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }
    const currencyInfo = currenciesData.find(c => c.code === currency) || CURRENCY_DATA[currency];
    const symbol = currencyInfo?.symbol || '$';
    const position = currencyInfo?.position || 'before';
    
    const formattedAmount = Number(amount).toLocaleString();
    
    if (position === 'before') {
      return `${symbol}${formattedAmount}`;
    } else {
      return `${formattedAmount} ${symbol}`;
    }
  };

  // Dynamic budget ranges based on currency
  const getBudgetRanges = () => {
    const symbol = getCurrencySymbol();
    const currencyMultipliers = {
      'USD': 1, 'EUR': 0.9, 'GBP': 0.8, 'JPY': 150, 'CNY': 7.2, 'INR': 83,
      'AUD': 1.5, 'CAD': 1.35, 'CHF': 0.88, 'KRW': 1300, 'SGD': 1.35,
      'MXN': 17, 'BRL': 5, 'ZAR': 19, 'AED': 3.67, 'SAR': 3.75,
      'NGN': 900, 'KES': 155, 'GHS': 12
    };
    
    const multiplier = currencyMultipliers[currency] || 1;
    
    return [
      { id: 'micro', range: `${symbol}${Math.round(100 * multiplier).toLocaleString()}-${Math.round(500 * multiplier).toLocaleString()}`, label: 'Micro Budget' },
      { id: 'startup', range: `${symbol}${Math.round(500 * multiplier).toLocaleString()}-${Math.round(2000 * multiplier).toLocaleString()}`, label: 'Startup Budget' },
      { id: 'small', range: `${symbol}${Math.round(2000 * multiplier).toLocaleString()}-${Math.round(10000 * multiplier).toLocaleString()}`, label: 'Small Business' },
      { id: 'medium', range: `${symbol}${Math.round(10000 * multiplier).toLocaleString()}-${Math.round(50000 * multiplier).toLocaleString()}`, label: 'Medium Business' },
      { id: 'large', range: `${symbol}${Math.round(50000 * multiplier).toLocaleString()}-${Math.round(200000 * multiplier).toLocaleString()}`, label: 'Large Business' },
      { id: 'enterprise', range: `${symbol}${Math.round(200000 * multiplier).toLocaleString()}+`, label: 'Enterprise' },
      { id: 'custom', range: 'Custom Amount', label: `Enter Custom Budget in ${currency}` }
    ];
  };

  // Save analysis function
  const saveAnalysis = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const analysisData = {
        businessInput,
        selectedCategory: selectedCategory === 'custom' ? customCategory : selectedCategory,
        businessModel: businessModel === 'custom' ? customBusinessModel : businessModel,
        country,
        currency,
        monthlyBudget,
        customBudget,
        primaryGoal: primaryGoal === 'custom' ? customPrimaryGoal : primaryGoal,
        targetAudience,
        experienceLevel,
        recommendations
      };

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ analysisData })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save analysis');
      }

      const result = await response.json();
      
      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(result.error || 'Save failed');
      }
      
    } catch (err) {
      logger.error('Save error:', err);
      setError(err.message || 'Failed to save analysis. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Export as image function
  const exportAsImage = async () => {
    if (!reportRef.current) return;

    try {
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
      loadingDiv.innerHTML = '<div class="text-white">Generating image...</div>';
      document.body.appendChild(loadingDiv);

      const originalBackground = reportRef.current.style.background;
      reportRef.current.style.background = 'linear-gradient(to bottom right, #111827, #1f2937, #374151)';
      
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#111827',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      reportRef.current.style.background = originalBackground;

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-platform-strategy-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        
        document.body.removeChild(loadingDiv);
      }, 'image/png');

    } catch (error) {
      logger.error('Error exporting image:', error);
      setError('Failed to export image. Please try again.');
    }
  };

  // Enhanced AI Analysis
  const analyzeBusinessAndRecommend = async () => {
    setIsAnalyzing(true);
    setError(null);
    setAnalysisProgress(0);
    
    try {
      const analysisSteps = [
        { step: `Analyzing business model and market position in ${country}...`, progress: 10 },
        { step: `Discovering optimal advertising platforms for ${country}...`, progress: 20 },
        { step: `Researching current market trends in ${currency}...`, progress: 35 },
        { step: `Analyzing competitor strategies in ${country} market...`, progress: 50 },
        { step: `Processing audience psychology and behavior patterns...`, progress: 65 },
        { step: `Evaluating platform performance trends in ${currency}...`, progress: 80 },
        { step: `Calculating optimal platform mix and budget allocation...`, progress: 90 },
        { step: `Generating cross-platform synergies...`, progress: 95 },
        { step: `Finalizing AI recommendations for ${country} market...`, progress: 100 }
      ];

      for (const { step, progress } of analysisSteps) {
        setAiAnalysisStep(step);
        setAnalysisProgress(progress);
        await new Promise(resolve => setTimeout(resolve, 600));
      }

      const authData = localStorage.getItem('sb-auth-token');
      const token = authData ? JSON.parse(authData).access_token : null;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const requestData = {
        business_description: businessInput,
        business_name: `${selectedCategory === 'custom' ? customCategory : selectedCategory} Business`,
        category: selectedCategory === 'custom' ? customCategory : selectedCategory,
        custom_category: selectedCategory === 'custom' ? customCategory : null,
        business_model: businessModel === 'custom' ? customBusinessModel : businessModel,
        custom_business_model: businessModel === 'custom' ? customBusinessModel : null,
        country: country,
        currency: currency,
        monthly_budget: monthlyBudget,
        custom_budget: customBudget,
        primary_goal: primaryGoal === 'custom' ? customPrimaryGoal : primaryGoal,
        custom_primary_goal: primaryGoal === 'custom' ? customPrimaryGoal : null,
        target_audience: targetAudience,
        experience_level: experienceLevel,
        user_preferences: {
          platforms_owned: platformsOwned,
          platforms_to_exclude: platformsToExclude
        },
        geographic_targets: [country]
      };

      const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate AI recommendations');
      }

      const results = await response.json();
      
      if (results.success) {
        setRecommendations(results.data);
        setCurrentStep(4);
        setActiveTab('overview');
      } else {
        throw new Error(results.error || 'Analysis failed');
      }
      
    } catch (err) {
      logger.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze business. Please try again.');
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      setAiAnalysisStep('');
    }
  };

// Simulate budget change
const handleBudgetSimulation = async (newBudget) => {
  try {
    const authData = localStorage.getItem('sb-auth-token');
    const token = authData ? JSON.parse(authData).access_token : null;
    
    const response = await fetch(`${BACKEND_URL}/api/platform-intelligence/simulate-budget`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        new_budget: newBudget,
        current_platforms: recommendations?.platforms || [],
        currency: recommendations?.currency || 'USD',
        country: recommendations?.country || 'United States'
      })
    });
    
    if (response.ok) {
      const result = await response.json();
      
      const platforms = result.data.platforms;
      const currency = result.data.currency || 'USD';
      const budget = result.data.simulated_budget;
      
      // Calculate averages from platforms
      const avgCpc = platforms.reduce((sum, p) => sum + (p.avg_cpc || 0) * (p.allocation || 0), 0) / 100;
      const avgRoas = platforms.reduce((sum, p) => sum + (p.avg_roas || 0) * (p.allocation || 0), 0) / 100;
      
      // Calculate monthly metrics based on new budget
      const month1Clicks = Math.round((budget * 0.7) / avgCpc);
      const month2Clicks = Math.round((budget * 0.9) / avgCpc);
      const month3Clicks = Math.round(budget / avgCpc);
      
      const month1Impressions = Math.round((budget * 0.7 / 15) * 1000);
      const month2Impressions = Math.round((budget * 0.9 / 12) * 1000);
      const month3Impressions = Math.round((budget / 10) * 1000);
      
      const month1Conversions = Math.round(month1Clicks * 0.015);
      const month2Conversions = Math.round(month2Clicks * 0.025);
      const month3Conversions = Math.round(month3Clicks * 0.035);
      
      // Q1 totals
      const q1TotalSpend = budget * 2.6;
      const q1TotalRevenue = q1TotalSpend * 3.5;
      const q1TotalConversions = month1Conversions + month2Conversions + month3Conversions;
      
      setRecommendations(prev => ({
        ...prev,
        platforms: platforms,
        totalBudget: budget,
        totalBudgetDisplay: formatCurrency(budget, currency),
        budgetScenarios: result.data.budget_scenarios,
        
        // Overview metrics
        averageROAS: avgRoas,
        
        // Update visualData
        visualData: {
          ...prev.visualData,
          efficiency_metrics: {
            ...prev.visualData?.efficiency_metrics,
            total_estimated_clicks: result.data.total_estimated_clicks,
            total_estimated_impressions: result.data.total_estimated_impressions,
            average_cpc: avgCpc,
            average_cpc_display: formatCurrency(avgCpc, currency),
            projected_roas: avgRoas
          },
          platform_distribution: platforms.map(p => ({
            name: p.platform_name,
            value: p.allocation,
            budget: p.budget,
            budget_display: p.budget_display
          }))
        },
        
        // Update performance predictions with recalculated metrics
        performancePredictions: {
          ...prev.performancePredictions,
          currency: currency,
          monthly_predictions: {
            month_1: {
              ...prev.performancePredictions?.monthly_predictions?.month_1,
              budget_deployment: budget * 0.7,
              budget_display: formatCurrency(budget * 0.7, currency),
              estimated_clicks: month1Clicks,
              estimated_impressions: month1Impressions,
              estimated_conversions: month1Conversions,
              expected_roas: 2.5
            },
            month_2: {
              ...prev.performancePredictions?.monthly_predictions?.month_2,
              budget_deployment: budget * 0.9,
              budget_display: formatCurrency(budget * 0.9, currency),
              estimated_clicks: month2Clicks,
              estimated_impressions: month2Impressions,
              estimated_conversions: month2Conversions,
              expected_roas: 3.5
            },
            month_3: {
              ...prev.performancePredictions?.monthly_predictions?.month_3,
              budget_deployment: budget,
              budget_display: formatCurrency(budget, currency),
              estimated_clicks: month3Clicks,
              estimated_impressions: month3Impressions,
              estimated_conversions: month3Conversions,
              expected_roas: avgRoas
            }
          },
          quarterly_projection: {
            q1_total: {
              total_spend: q1TotalSpend,
              total_spend_display: formatCurrency(q1TotalSpend, currency),
              total_revenue: q1TotalRevenue,
              total_revenue_display: formatCurrency(q1TotalRevenue, currency),
              total_conversions: q1TotalConversions,
              average_roas: 3.5
            },
            q2_projection: {
              ...prev.performancePredictions?.quarterly_projection?.q2_projection,
              recommended_budget: budget * 1.5,
              recommended_budget_display: formatCurrency(budget * 1.5, currency)
            }
          },
          kpi_targets: {
            week_2: { roas: 2.0, cpa_index: 120 },
            week_4: { roas: 3.0, cpa_index: 100 },
            week_8: { roas: 4.0, cpa_index: 85 },
            week_12: { roas: avgRoas, cpa_index: 75 }
          }
        },
        
        // Update strategic roadmap
        strategicRoadmap: {
          ...prev.strategicRoadmap,
          implementation_phases: {
            phase_1: {
              ...prev.strategicRoadmap?.implementation_phases?.phase_1,
              budget_deployment: budget * 0.15,
              budget_display: formatCurrency(budget * 0.15, currency)
            },
            phase_2: {
              ...prev.strategicRoadmap?.implementation_phases?.phase_2,
              budget_deployment: budget * 0.40,
              budget_display: formatCurrency(budget * 0.40, currency)
            },
            phase_3: {
              ...prev.strategicRoadmap?.implementation_phases?.phase_3,
              budget_deployment: budget * 0.70,
              budget_display: formatCurrency(budget * 0.70, currency)
            },
            phase_4: {
              ...prev.strategicRoadmap?.implementation_phases?.phase_4,
              budget_deployment: budget,
              budget_display: formatCurrency(budget, currency)
            }
          }
        },
        
        // Update risk assessment - recalculate based on new budget
        riskAssessment: {
          ...prev.riskAssessment,
          identified_risks: prev.riskAssessment?.identified_risks?.map(risk => {
            if (risk.risk_type === 'budget_limitation') {
              // Update budget-related risk
              const isLowBudget = budget < 2000;
              return {
                ...risk,
                description: `${formatCurrency(budget, currency)}/month budget ${isLowBudget ? 'restricts' : 'allows'} multi-platform testing`,
                severity: isLowBudget ? 'high' : budget < 5000 ? 'medium' : 'low',
                probability: isLowBudget ? 100 : budget < 5000 ? 60 : 30
              };
            }
            return risk;
          }),
          overall_risk_score: budget < 2000 ? 84 : budget < 5000 ? 60 : budget < 10000 ? 45 : 30,
          overall_risk_level: budget < 2000 ? 'high' : budget < 5000 ? 'medium' : 'low'
        },
        
        // Update competitive analysis budget references
        competitiveAnalysis: {
          ...prev.competitiveAnalysis,
          detailed_analysis: {
            ...prev.competitiveAnalysis?.detailed_analysis,
            market_share_potential: {
              ...prev.competitiveAnalysis?.detailed_analysis?.market_share_potential,
              investment_needed: formatCurrency(budget, currency)
            }
          }
        },
        
        // Track simulation state
        isSimulated: true,
        simulatedBudget: budget,
        originalBudget: prev.isSimulated ? prev.originalBudget : prev.totalBudget
      }));
    }
  } catch (error) {
    logger.error('Budget simulation error:', error);
  }
};

  // Navigation functions
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      analyzeBusinessAndRecommend();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        const categoryValid = selectedCategory && (selectedCategory !== 'custom' || customCategory.trim());
        const modelValid = businessModel && (businessModel !== 'custom' || customBusinessModel.trim());
        return businessInput.trim() && categoryValid && modelValid && (country || '').trim();
      case 2:
        const budgetValid = monthlyBudget && (monthlyBudget !== 'custom' || customBudget.trim());
        const goalValid = primaryGoal && (primaryGoal !== 'custom' || customPrimaryGoal.trim());
        return budgetValid && goalValid && experienceLevel;
      case 3:
        return targetAudience.trim();
      default:
        return false;
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setBusinessInput('');
    setSelectedCategory('');
    setCustomCategory('');
    setBusinessModel('');
    setCustomBusinessModel('');
    setCountry('United States');
    setCurrency('USD');
    setMonthlyBudget('');
    setCustomBudget('');
    setPrimaryGoal('');
    setCustomPrimaryGoal('');
    setTargetAudience('');
    setExperienceLevel('');
    setRecommendations(null);
    setShowImplementationPlan(null);
    setShowAIInsights(null);
    setError(null);
    setActiveTab('platforms');
    setExpandedPlatform(null);
    setUseCustomLocation(false);
  };

  // Helper functions for styling
  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-emerald-500';
    if (confidence >= 80) return 'text-blue-500';
    if (confidence >= 70) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up' || direction === 'growing' || direction === 'explosive') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (direction === 'down' || direction === 'declining') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getCompetitionColor = (level) => {
    const colors = {
      'low': 'text-emerald-500',
      'medium': 'text-yellow-500',
      'high': 'text-orange-500',
      'extreme': 'text-red-500'
    };
    return colors[level] || 'text-gray-500';
  };

  // Render confidence meter
  const ConfidenceMeter = ({ score, label }) => {
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative">
          <svg className="transform -rotate-90 w-24 h-24">
            <circle
              cx="48"
              cy="48"
              r={radius}
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-gray-700"
            />
            <circle
              cx="48"
              cy="48"
              r={radius}
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className={getConfidenceColor(score)}
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-2xl font-bold ${getConfidenceColor(score)}`}>{score}%</span>
          </div>
        </div>
        <span className="text-xs text-gray-400 mt-2">{label}</span>
      </div>
    );
  };

  // Render platform performance chart
 const PlatformPerformanceChart = ({ platform }) => {
  const avgCpc = platform.avg_cpc || 0;
  const avgRoas = platform.avg_roas || 3.5;
  const opportunityScore = platform.opportunity_score || platform.confidence || 75;
  
  // Fix cost efficiency - lower CPC = higher efficiency
  const maxExpectedCpc = 500;
  const costEfficiency = Math.max(0, Math.min(100, ((maxExpectedCpc - avgCpc) / maxExpectedCpc) * 100));
  
  const metrics = [
    { label: 'ROI', value: Math.min(100, avgRoas * 20), max: 100 },
    { label: 'Reach', value: opportunityScore, max: 100 },
    { label: 'Cost Efficiency', value: costEfficiency, max: 100 },
    { label: 'Growth', value: platform.trend_direction === 'up' || platform.trend_direction === 'growing' ? 85 : platform.trend_direction === 'stable' ? 65 : 45, max: 100 }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {metrics.map((metric, idx) => (
        <div key={idx} className="bg-gray-800/50 rounded-lg p-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-400">{metric.label}</span>
            <span className="text-xs font-semibold text-white">{Math.round(metric.value)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${metric.value}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

  // Render market intelligence visualization
  const MarketIntelligenceVisual = ({ data }) => {
    if (!data) return null;

    const metrics = data.visual_metrics || {};
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-300">Opportunity</span>
          </div>
          <div className="text-2xl font-bold text-white">{metrics.opportunity_score || 0}%</div>
          <div className="text-xs text-gray-400 mt-1">Market potential</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-sm font-medium text-gray-300">Competition</span>
          </div>
          <div className="text-2xl font-bold text-white">{metrics.competitive_intensity || 0}%</div>
          <div className="text-xs text-gray-400 mt-1">Market saturation</div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            <span className="text-sm font-medium text-gray-300">Timing</span>
          </div>
          <div className="text-2xl font-bold text-white">{metrics.market_timing || 0}%</div>
          <div className="text-xs text-gray-400 mt-1">Entry timing score</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-gray-300">Success Rate</span>
          </div>
          <div className="text-2xl font-bold text-white">{metrics.success_probability || 0}%</div>
          <div className="text-xs text-gray-400 mt-1">Win probability</div>
        </div>
      </div>
    );
  };

  // Render journey map visualization
  const CustomerJourneyVisualization = ({ journeyMap }) => {
    if (!journeyMap || !journeyMap.stages) return null;

    const stages = journeyMap.stages;

    return (
      <div className="relative">
        <div className="flex items-center justify-between mb-8">
          {stages.map((stage, idx) => (
            <div key={idx} className="flex-1 relative">
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  idx === 0 ? 'bg-purple-500' : 
                  idx === 1 ? 'bg-blue-500' : 
                  idx === 2 ? 'bg-emerald-500' : 
                  'bg-orange-500'
                }`}>
                  {idx === 0 ? <Eye className="w-6 h-6 text-white" /> :
                   idx === 1 ? <Search className="w-6 h-6 text-white" /> :
                   idx === 2 ? <Target className="w-6 h-6 text-white" /> :
                   <Award className="w-6 h-6 text-white" />}
                </div>
                <span className="text-sm font-semibold text-white mt-2">{stage.stage}</span>
                <span className="text-xs text-gray-400">{stage.duration}</span>
              </div>
              {idx < stages.length - 1 && (
                <div className="absolute top-6 left-full w-full h-0.5 bg-gray-600 -translate-x-1/2">
                  <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {stages.map((stage, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-white mb-3">{stage.stage}</h4>
              
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-400">Platforms</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {stage.platforms?.map((platform, pidx) => (
                      <span key={pidx} className="text-xs bg-white/10 px-2 py-1 rounded">
                        {getPlatformIcon(platform)} {platform}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-xs text-gray-400">Key Metrics</span>
                  <div className="space-y-1 mt-1">
                    {stage.metrics?.map((metric, midx) => (
                      <div key={midx} className="text-xs text-gray-300">• {metric}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render risk matrix
  const RiskMatrix = ({ risks }) => {
    if (!risks || !risks.identified_risks) return null;

    const risksByCategory = risks.identified_risks.reduce((acc, risk) => {
      if (!acc[risk.category]) acc[risk.category] = [];
      acc[risk.category].push(risk);
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        {Object.entries(risksByCategory).map(([categoryName, categoryRisks]) => (
          <div key={categoryName} className="bg-white/5 border border-white/10 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-white mb-3 capitalize">{categoryName} Risks</h4>
            <div className="space-y-2">
              {categoryRisks.map((risk, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 ${
                    risk.severity === 'high' ? 'bg-red-500' :
                    risk.severity === 'medium' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white">{risk.description}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        risk.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                        risk.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {risk.probability}% likely
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{risk.impact}</p>
                    {risk.mitigation_strategies && (
                      <div className="mt-2">
                        <span className="text-xs text-purple-400">Mitigation:</span>
                        <ul className="list-disc list-inside text-xs text-gray-300 mt-1">
                          {risk.mitigation_strategies.slice(0, 2).map((strategy, sidx) => (
                            <li key={sidx}>{strategy}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Enhanced loading state
  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 flex items-center justify-center p-4">
        <div className="text-center text-white max-w-md w-full">
          <div className="relative w-32 h-32 mx-auto mb-8">
            <div className="absolute inset-0">
              <div className="absolute top-0 left-1/2 w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
              <div className="absolute top-1/4 left-0 w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-100" />
              <div className="absolute top-1/4 right-0 w-2 h-2 bg-emerald-500 rounded-full animate-pulse delay-200" />
              <div className="absolute bottom-1/4 left-0 w-2 h-2 bg-orange-500 rounded-full animate-pulse delay-300" />
              <div className="absolute bottom-1/4 right-0 w-2 h-2 bg-pink-500 rounded-full animate-pulse delay-400" />
              <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-yellow-500 rounded-full animate-pulse delay-500" />
              
              <Brain className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 text-purple-500" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent">
            AI Strategic Intelligence Engine
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Analyzing {country} market with real-time data...
          </p>
          
          <div className="relative">
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-emerald-500 transition-all duration-500 ease-out relative"
                style={{ width: `${analysisProgress}%` }}
              >
                <div className="absolute right-0 top-0 h-full w-4 bg-white/20 animate-pulse" />
              </div>
            </div>
            <div className="absolute -top-6 left-0 w-full">
              <div 
                className="text-xs font-semibold text-purple-500 transition-all duration-500"
                style={{ marginLeft: `${Math.min(analysisProgress - 5, 85)}%` }}
              >
                {analysisProgress}%
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center animate-pulse">
              <Cpu className="w-4 h-4 text-purple-500" />
            </div>
            <span className="text-sm text-gray-300">{aiAnalysisStep}</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-8 max-w-sm mx-auto">
            <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <Globe className="w-3 h-3 text-purple-500" />
              <span>Real-Time Market Data</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <Shield className="w-3 h-3 text-blue-500" />
              <span>Competitive Intelligence</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <Users className="w-3 h-3 text-emerald-500" />
              <span>Audience Psychology</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <Layers className="w-3 h-3 text-orange-500" />
              <span>Platform Discovery</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced results view
  if (recommendations) {
    const hasAIEnhancements = recommendations.marketIntelligence || recommendations.audiencePsychology;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700">
       
      {/* Enhanced Header */}
      <div className="bg-gray-900/95 backdrop-blur-md border-b border-purple-500/20 sticky top-0 z-20">
        <div className="w-full px-4 py-3">
          {/* Top row - Back button and action buttons */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={resetForm}
              className="p-2 bg-purple-500/20 border border-purple-500 rounded-lg text-purple-300 hover:bg-purple-500/30 transition-all"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={openSavedAnalyses}
                className="p-2 bg-blue-500/20 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-all"
                title="View Saved Analyses"
              >
                <FileText className="w-4 h-4 text-blue-400" />
              </button>
              <button
                onClick={saveAnalysis}
                disabled={isSaving}
                className={`p-2 rounded-lg transition-all ${
                  saveSuccess 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-white/5 hover:bg-white/10 text-gray-400'
                }`}
                title="Save Analysis"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={exportAsImage}
                className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-all"
                title="Download as Image"
              >
                <Image className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
          
          {/* Title row */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-5 h-5 text-purple-500 flex-shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-white">AI Strategic Intelligence Report</h1>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="text-emerald-500 font-semibold">
                {recommendations.overallConfidence}% confidence
              </span>
              <span className="text-blue-400 truncate max-w-[200px] sm:max-w-none">
                {recommendations.country} | {recommendations.currency}
              </span>
              {hasAIEnhancements && (
                <span className="text-purple-500 font-semibold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI Enhanced
                </span>
              )}
              {recommendations.experienceLevel && (
                <span className="text-gray-400">
                  ({recommendations.experienceLevel})
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="w-full px-2 overflow-x-auto">
          <div className="flex gap-1 pb-1 min-w-max">
            {[
              { id: 'overview', label: 'Overview', icon: Gauge },
              { id: 'platforms', label: 'Platforms', icon: Layers },
              { id: 'market', label: 'Market', icon: Globe },
              { id: 'audience', label: 'Audience', icon: Users },
              { id: 'competitive', label: 'Competitive', icon: Swords },
              { id: 'journey', label: 'Journey', icon: Navigation },
              { id: 'roadmap', label: 'Roadmap', icon: Map },
              { id: 'predictions', label: 'Predictions', icon: TrendingUp },
              { id: 'risks', label: 'Risks', icon: Shield }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-purple-400 border-purple-500 bg-purple-500/10'
                    : 'text-gray-400 border-transparent hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-3 h-3" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

        <div className="w-full max-w-7xl mx-auto px-4 py-4 overflow-x-hidden" ref={reportRef}>
          {error && (
            <div className="flex items-center bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
              <div className="flex-1">
                <p className="text-sm text-red-500">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-500 text-lg ml-3">✕</button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics Dashboard */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <TrendingUp className="w-8 h-8 text-emerald-500" />
                    <span className="text-xs text-emerald-400 font-semibold">+15% vs avg</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{recommendations.averageROAS}x</div>
                  <div className="text-sm text-gray-400 mt-1">Predicted ROAS</div>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-8 h-8 text-blue-500" />
                    <span className="text-xs text-blue-400 font-semibold">{recommendations.currency}</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{recommendations.totalBudgetDisplay || formatCurrency(recommendations.totalBudget)}</div>
                  <div className="text-sm text-gray-400 mt-1">Monthly Budget</div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Target className="w-8 h-8 text-purple-500" />
                    <span className="text-xs text-purple-400 font-semibold">AI Score</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{recommendations.overallConfidence}%</div>
                  <div className="text-sm text-gray-400 mt-1">Success Confidence</div>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Globe className="w-8 h-8 text-orange-500" />
                    <span className="text-xs text-orange-400 font-semibold">{recommendations.country}</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{recommendations.platforms?.length || 0}</div>
                  <div className="text-sm text-gray-400 mt-1">Platforms Discovered</div>
                </div>
              </div>

              {/* Experience Level Guidance */}
              {recommendations.experienceGuidance && (
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-indigo-500" />
                    {recommendations.experienceGuidance.title}
                  </h2>
                  <p className="text-gray-300 mb-4">{recommendations.experienceGuidance.overview}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-indigo-400 mb-2">Recommended Approach</h3>
                      <p className="text-sm text-gray-300">{recommendations.experienceGuidance.recommended_approach}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-indigo-400 mb-2">When to Expand</h3>
                      <p className="text-sm text-gray-300">{recommendations.experienceGuidance.when_to_expand}</p>
                    </div>
                  </div>

                  {recommendations.experienceGuidance.common_mistakes_to_avoid && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-red-400 mb-2">Common Mistakes to Avoid</h3>
                      <ul className="space-y-1">
                        {recommendations.experienceGuidance.common_mistakes_to_avoid.map((mistake, idx) => (
                          <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            {mistake}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Business Intelligence Summary */}
              {recommendations.businessAnalysis && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-500" />
                    Business Intelligence Summary
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-400">Business Type</span>
                      <p className="text-lg font-semibold text-white">{recommendations.businessAnalysis.businessType}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Sales Cycle</span>
                      <p className="text-lg font-semibold text-white">{recommendations.businessAnalysis.salesCycle}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-400">Market Maturity</span>
                      <p className="text-lg font-semibold text-white capitalize">{recommendations.businessAnalysis.marketMaturity}</p>
                    </div>
                  </div>
                  
                  {recommendations.businessAnalysis.keyInsights && (
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-purple-400 mb-2">Key Strategic Insights</h3>
                      <ul className="space-y-2">
                        {recommendations.businessAnalysis.keyInsights.map((insight, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Lightbulb className="w-4 h-4 text-purple-500 mt-0.5" />
                            <span>{insight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Platforms Tab */}
          {activeTab === 'platforms' && (
            <div className="space-y-6">
              {/* Platform Mix Visualization */}
              {recommendations.visualData && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-white mb-4">Budget Allocation Strategy</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Platform Distribution */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Platform Budget Distribution</h3>
                      {recommendations.visualData.platform_distribution?.map((platform, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="text-2xl">{getPlatformIcon(platform.name)}</div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-white">{platform.name}</span>
                              <span className="text-sm text-gray-400">
                                {platform.budget_display || formatCurrency(platform.budget)} ({platform.value}%)
                              </span>
                            </div>
                            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${platform.value}%`,
                                  backgroundColor: platform.color 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Efficiency Metrics */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white mb-3">Campaign Projections</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-white">
                            {recommendations.visualData.efficiency_metrics?.total_estimated_clicks?.toLocaleString() || 0}
                          </div>
                          <div className="text-sm text-gray-400">Est. Monthly Clicks</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-white">
                            {((recommendations.visualData.efficiency_metrics?.total_estimated_impressions || 0) / 1000).toFixed(0)}K
                          </div>
                          <div className="text-sm text-gray-400">Est. Impressions</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-white">
                            {recommendations.visualData.efficiency_metrics?.average_cpc_display || formatCurrency(recommendations.visualData.efficiency_metrics?.average_cpc || 0)}
                          </div>
                          <div className="text-sm text-gray-400">Avg. CPC</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-emerald-500">
                            {recommendations.visualData.efficiency_metrics?.projected_roas?.toFixed(1) || 0}x
                          </div>
                          <div className="text-sm text-gray-400">Projected ROAS</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Budget Simulator */}
              <BudgetSimulator 
                currentBudget={recommendations.totalBudget}
                currency={recommendations.currency}
                platforms={recommendations.platforms}
                budgetScenarios={recommendations.budgetScenarios}
                onSimulate={handleBudgetSimulation}
              />

              {/* Seasonality Insights */}
              <SeasonalityInsights 
                seasonalityData={recommendations.seasonalityData}
                countryName={recommendations.country}
                category={selectedCategory}
              />

              {/* Platform Cards */}
              <div className="space-y-4">
                {(recommendations.platforms || []).map((platform, index) => (
                  <div
                    key={index}
                    className={`bg-white/5 border rounded-xl overflow-hidden transition-all ${
                      index === 0 ? 'border-purple-500 bg-purple-500/5' : 'border-white/10'
                    }`}
                  >
                    {/* Platform Header */}
                    <div
                      className="p-6 cursor-pointer"
                      onClick={() => setExpandedPlatform(expandedPlatform === index ? null : index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-xl text-2xl">
                            {getPlatformIcon(platform.platform_name)}
                          </div>
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-xl font-bold text-white">{platform.platform_name}</h3>
                              {index === 0 && (
                                <span className="text-xs font-semibold text-emerald-500 bg-emerald-500/20 px-2 py-1 rounded">
                                  PRIMARY
                                </span>
                              )}
                              {platform.platform_type && (
                                <span className="text-xs font-semibold text-blue-500 bg-blue-500/20 px-2 py-1 rounded">
                                  {platform.platform_type.toUpperCase()}
                                </span>
                              )}
                              {platform.has_existing_account && (
                                <span className="text-xs font-semibold text-green-500 bg-green-500/20 px-2 py-1 rounded">
                                  YOU OWN THIS
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400 mt-1">{platform.ai_reasoning || platform.why_this_platform || platform.why_recommended}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${getConfidenceColor(platform.confidence)}`}>
                              {platform.confidence}%
                            </div>
                            <div className="text-xs text-gray-400">AI Confidence</div>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedPlatform === index ? 'rotate-180' : ''
                          }`} />
                        </div>
                      </div>

                      {/* Quick Stats Bar */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-white/10">
                        <div>
                          <span className="text-xs text-gray-400">Budget</span>
                          <p className="text-lg font-semibold text-white">
                            {platform.budget_display || formatCurrency(platform.budget || 0)}/mo
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Allocation</span>
                          <p className="text-lg font-semibold text-white">{platform.allocation}%</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">
                            {experienceLevel === 'beginner' ? (
                              <GlossaryTooltip term="CPC">Avg. </GlossaryTooltip>
                            ) : 'Avg. CPC'}
                          </span>
                          <p className="text-lg font-semibold text-white">
                            {platform.avg_cpc_display || formatCurrency(platform.avg_cpc || 0)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">
                            {experienceLevel === 'beginner' ? (
                              <GlossaryTooltip term="ROAS">Pred. </GlossaryTooltip>
                            ) : 'Pred. ROAS'}
                          </span>
                          <p className="text-lg font-semibold text-emerald-500">{(platform.avg_roas || 3.5).toFixed(1)}x</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            Trend {getTrendIcon(platform.trend_direction)}
                          </span>
                          <p className={`text-lg font-semibold ${getCompetitionColor(platform.competition_level)}`}>
                            {platform.competition_level} competition
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Platform Details */}
                    {expandedPlatform === index && (
                      <div className="px-6 pb-6 border-t border-white/10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                          {/* Strategic Approach */}
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-semibold text-purple-400 mb-2">Strategic Approach</h4>
                              <p className="text-sm text-gray-300">{platform.strategic_approach || platform.strategic_insights}</p>
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold text-purple-400 mb-2">Expected Results</h4>
                              <p className="text-sm text-gray-300">{platform.expected_results || platform.expected_outcomes}</p>
                            </div>

                            {platform.quick_wins && (
                              <div>
                                <h4 className="text-sm font-semibold text-emerald-400 mb-2">Quick Wins (First 30 Days)</h4>
                                <ul className="space-y-1">
                                  {platform.quick_wins.map((win, widx) => (
                                    <li key={widx} className="flex items-start gap-2 text-sm text-gray-300">
                                      <Zap className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                      {win}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {platform.first_steps && (
                              <div>
                                <h4 className="text-sm font-semibold text-blue-400 mb-2">First Steps to Launch</h4>
                                <ol className="space-y-2">
                                  {platform.first_steps.map((step, sidx) => (
                                    <li key={sidx} className="flex items-start gap-2 text-sm text-gray-300">
                                      <span className="w-5 h-5 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-blue-400">
                                        {sidx + 1}
                                      </span>
                                      {step}
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </div>

                          {/* Performance Metrics */}
                          <div className="space-y-4">
                            <PlatformPerformanceChart platform={platform} />
                            
                            {/* Best Ad Formats */}
                            {platform.best_ad_formats && platform.best_ad_formats.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-purple-400 mb-2">Recommended Ad Formats</h4>
                                <div className="flex flex-wrap gap-2">
                                  {platform.best_ad_formats.map((format, fidx) => (
                                    <span key={fidx} className="text-xs bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">
                                      {format}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Targeting Options */}
                            {platform.targeting_options && platform.targeting_options.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-blue-400 mb-2">Key Targeting Options</h4>
                                <div className="flex flex-wrap gap-2">
                                  {platform.targeting_options.map((option, oidx) => (
                                    <span key={oidx} className="text-xs bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">
                                      {option}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Integration Synergies */}
                            {platform.integration_synergies && platform.integration_synergies.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-orange-400 mb-2">Platform Synergies</h4>
                                <div className="space-y-2">
                                  {platform.integration_synergies.map((synergy, syidx) => (
                                    <div key={syidx} className="flex items-center gap-2 text-sm text-gray-300">
                                      <Network className="w-4 h-4 text-orange-500" />
                                      {synergy}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Audience Match */}
                            {platform.audience_match_score && (
                              <div className="bg-gray-800/50 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-gray-300 mb-3">Audience Fit Analysis</h4>
                                <div className="flex items-center gap-4">
                                  <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-xs text-gray-400">Audience Match</span>
                                      <span className="text-xs font-semibold text-emerald-400">{platform.audience_match_score}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-emerald-500 rounded-full"
                                        style={{ width: `${platform.audience_match_score}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                {platform.audience_overlap_reason && (
                                  <p className="text-xs text-gray-400 mt-2">{platform.audience_overlap_reason}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Implementation Plan CTA */}
                        <div className="mt-6 flex items-center justify-between p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Zap className="w-5 h-5 text-purple-500" />
                            <span className="text-sm text-gray-300">Ready to implement this platform strategy?</span>
                          </div>
                          <button
                            onClick={() => setShowImplementationPlan(platform)}
                            className="bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-600 transition-all flex items-center gap-2"
                          >
                            View Implementation Plan
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Why Not Section - Excluded Platforms */}
              <WhyNotSection excludedPlatforms={recommendations.excludedPlatforms} />
            </div>
          )}

      {/* Market Tab */}
      {activeTab === 'market' && (
        <div className="space-y-6">
          {recommendations.marketIntelligence ? (
            <>
              {/* Visual Metrics */}
              <MarketIntelligenceVisual data={recommendations.marketIntelligence} />
              
              {/* Market Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  {recommendations.country} Market Analysis
                </h2>
                
                <p className="text-gray-300 mb-6">{recommendations.marketIntelligence.summary}</p>
                
                {/* Key Findings from detailed_analysis */}
                {recommendations.marketIntelligence.detailed_analysis?.industry_insights && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Industry Growth</span>
                      <p className="text-xl font-bold text-emerald-400">
                        {recommendations.marketIntelligence.detailed_analysis.industry_insights.growth_rate}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Digital Ad Spend Trend</span>
                      <p className="text-xl font-bold text-blue-400">
                        {recommendations.marketIntelligence.detailed_analysis.industry_insights.digital_ad_spend_trend}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Customer Acquisition Cost</span>
                      <p className="text-xl font-bold text-purple-400">
                        {recommendations.marketIntelligence.detailed_analysis.industry_insights.customer_acquisition_costs}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Conversion Benchmarks</span>
                      <p className="text-xl font-bold text-orange-400">
                        {recommendations.marketIntelligence.detailed_analysis.industry_insights.conversion_rate_benchmarks}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Strategic Recommendations */}
              {recommendations.marketIntelligence.detailed_analysis?.strategic_recommendations && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Strategic Recommendations
                  </h2>
                  
                  <div className="space-y-4">
                    {recommendations.marketIntelligence.detailed_analysis.strategic_recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        rec.priority === 'high' 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-blue-500/10 border-blue-500/30'
                      }`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${
                              rec.priority === 'high' 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {rec.priority.toUpperCase()} PRIORITY
                            </span>
                            <span className="text-xs text-gray-400">{rec.timeline}</span>
                          </div>
                        </div>
                        <p className="text-white font-medium mb-2">{rec.recommendation}</p>
                        <p className="text-sm text-gray-300 mb-2">
                          <span className="text-emerald-400">Expected Impact:</span> {rec.expected_impact}
                        </p>
                        <p className="text-xs text-gray-400">
                          <span className="text-purple-400">Data Backing:</span> {rec.data_backing}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Platform Landscape - Rising Platforms */}
              {recommendations.marketIntelligence.detailed_analysis?.platform_landscape?.rising_platforms && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Rising Platforms in {recommendations.country}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendations.marketIntelligence.detailed_analysis.platform_landscape.rising_platforms.map((platform, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{getPlatformIcon(platform.platform)}</span>
                            <span className="text-white font-semibold">{platform.platform}</span>
                          </div>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                            {platform.fit_score}% fit
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Market Share:</span>
                            <span className="text-white">{platform.current_market_share}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Growth Rate:</span>
                            <span className="text-emerald-400">{platform.growth_rate}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Avg Costs:</span>
                            <span className="text-white">{platform.avg_costs}</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-300 mt-3">{platform.opportunity}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Algorithm Updates */}
              {recommendations.marketIntelligence.detailed_analysis?.platform_landscape?.algorithm_updates && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-purple-500" />
                    Recent Algorithm Updates
                  </h2>
                  
                  <div className="space-y-4">
                {recommendations.marketIntelligence.detailed_analysis.platform_landscape.algorithm_updates.map((update, idx) => (
                  <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{getPlatformIcon(update.platform)}</span>
                      <span className="text-white font-semibold">{update.platform}</span>
                      {update.date && update.date.trim() !== '' && (
                        <span className="text-xs text-gray-400">({update.date})</span>
                      )}
                    </div>
                    <p className="text-sm text-white mb-2">{update.update}</p>
                    <p className="text-xs text-gray-300 mb-2">
                      <span className="text-orange-400">Impact:</span> {update.impact}
                    </p>
                    <p className="text-xs text-gray-400">Source: {update.source}</p>
                  </div>
                ))}
              </div>
                </div>
              )}

              {/* Seasonal Factors */}
              {recommendations.marketIntelligence.detailed_analysis?.seasonal_factors && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    Seasonal Factors
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-orange-400 mb-3">Next 3 Months</h3>
                      <div className="space-y-2">
                        {recommendations.marketIntelligence.detailed_analysis.seasonal_factors.next_3_months?.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Calendar className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-yellow-400 mb-3">Peak Seasons</h3>
                      <div className="space-y-2">
                        {recommendations.marketIntelligence.detailed_analysis.seasonal_factors.peak_seasons?.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <TrendingUp className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {recommendations.marketIntelligence.detailed_analysis.seasonal_factors.planning_considerations && (
                    <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                      <h3 className="text-sm font-semibold text-orange-400 mb-2">Planning Considerations</h3>
                      <ul className="space-y-1">
                        {recommendations.marketIntelligence.detailed_analysis.seasonal_factors.planning_considerations.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Budget Intelligence */}
              {recommendations.marketIntelligence.detailed_analysis?.budget_intelligence && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                    Budget Intelligence
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Typical Market Budgets</span>
                      <p className="text-lg font-bold text-white">
                        {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.typical_budgets_in_market}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Expected Market Share</span>
                      <p className="text-lg font-bold text-emerald-400">
                        {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.expected_market_share}
                      </p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <span className="text-xs text-gray-400">Optimal Allocation</span>
                      <p className="text-sm font-medium text-white">
                        {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.optimal_allocation_strategy}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <h3 className="text-sm font-semibold text-emerald-400 mb-2">Scaling Thresholds</h3>
                    <p className="text-sm text-gray-300">
                      {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.scaling_thresholds}
                    </p>
                  </div>

                  {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.efficiency_recommendations && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-blue-400 mb-2">Efficiency Recommendations</h3>
                      <ul className="space-y-2">
                        {recommendations.marketIntelligence.detailed_analysis.budget_intelligence.efficiency_recommendations.map((rec, idx) => (
                          <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                            <Zap className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Regional Specifics */}
              {recommendations.marketIntelligence.detailed_analysis?.regional_specifics && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Map className="w-5 h-5 text-purple-500" />
                    Regional Specifics - {recommendations.country}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-purple-400 mb-2">Local Regulations</h3>
                      <p className="text-sm text-gray-300">
                        {recommendations.marketIntelligence.detailed_analysis.regional_specifics.local_regulations}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-blue-400 mb-2">Cultural Considerations</h3>
                      <p className="text-sm text-gray-300">
                        {recommendations.marketIntelligence.detailed_analysis.regional_specifics.cultural_considerations}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-400 mb-2">Payment Preferences</h3>
                      <p className="text-sm text-gray-300">
                        {recommendations.marketIntelligence.detailed_analysis.regional_specifics.payment_preferences}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-orange-400 mb-2">Language Considerations</h3>
                      <p className="text-sm text-gray-300">
                        {recommendations.marketIntelligence.detailed_analysis.regional_specifics.language_considerations}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Competitive Landscape Summary */}
              {recommendations.marketIntelligence.detailed_analysis?.competitive_landscape_summary && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-red-500" />
                    Competitive Landscape
                  </h2>
                  <p className="text-gray-300">
                    {recommendations.marketIntelligence.detailed_analysis.competitive_landscape_summary}
                  </p>
                  
                  {recommendations.marketIntelligence.detailed_analysis.key_success_factors && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-emerald-400 mb-2">Key Success Factors</h3>
                      <div className="flex flex-wrap gap-2">
                        {recommendations.marketIntelligence.detailed_analysis.key_success_factors.map((factor, idx) => (
                          <span key={idx} className="text-sm bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Market Trends */}
              {recommendations.marketIntelligence.trends_data && recommendations.marketIntelligence.trends_data.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Market Trends
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendations.marketIntelligence.trends_data.map((trend, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        trend.type === 'opportunity' 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-orange-500/10 border-orange-500/30'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            trend.type === 'opportunity' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-orange-500/20 text-orange-400'
                          }`}>
                            {trend.type.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-400">{trend.timeframe}</span>
                        </div>
                        <p className="text-sm text-white">{trend.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Market Intelligence Not Available</h3>
              <p className="text-sm text-gray-400">
                Market intelligence data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}

       {/* Audience Tab */}
      {activeTab === 'audience' && (
        <div className="space-y-6">
          {recommendations.audiencePsychology ? (
            <>
              {/* Audience Psychology Overview */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Audience Psychology Analysis
                </h2>
                
                <p className="text-gray-300 mb-6">{recommendations.audiencePsychology.summary}</p>
              </div>

              {/* Audience Segments */}
              {recommendations.audiencePsychology.detailed_analysis?.audience_segments && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    Target Audience Segments
                  </h2>
                  
                  <div className="space-y-6">
                    {recommendations.audiencePsychology.detailed_analysis.audience_segments.map((segment, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-lg p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-white">{segment.segment_name}</h3>
                          <span className="text-sm bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full">
                            {segment.size_percentage}% of audience
                          </span>
                        </div>
                        
                        {/* Psychographic Profile */}
                        {segment.psychographic_profile && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <h4 className="text-sm font-semibold text-purple-400 mb-2">Values</h4>
                              <div className="flex flex-wrap gap-2">
                                {segment.psychographic_profile.values?.map((value, vidx) => (
                                  <span key={vidx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                                    {value}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-blue-400 mb-2">Lifestyle</h4>
                              <p className="text-sm text-gray-300">{segment.psychographic_profile.lifestyle}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-emerald-400 mb-2">Aspirations</h4>
                              <ul className="space-y-1">
                                {segment.psychographic_profile.aspirations?.map((asp, aidx) => (
                                  <li key={aidx} className="text-sm text-gray-300 flex items-start gap-2">
                                    <ArrowUpRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    {asp}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-red-400 mb-2">Fears</h4>
                              <ul className="space-y-1">
                                {segment.psychographic_profile.fears?.map((fear, fidx) => (
                                  <li key={fidx} className="text-sm text-gray-300 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                    {fear}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                        
                        {/* Decision Psychology */}
                        {segment.decision_psychology && (
                          <div className="border-t border-gray-700 pt-4 mt-4">
                            <h4 className="text-sm font-semibold text-orange-400 mb-3">Decision Psychology</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <span className="text-xs text-gray-400">Primary Motivation</span>
                                <p className="text-sm text-white">{segment.decision_psychology.primary_motivation}</p>
                              </div>
                              
                              <div>
                                <span className="text-xs text-gray-400">Price Sensitivity</span>
                                <p className="text-sm text-white">{segment.decision_psychology.price_sensitivity}</p>
                              </div>
                            </div>
                            
                            {segment.decision_psychology.decision_triggers && (
                              <div className="mt-3">
                                <span className="text-xs text-gray-400">Decision Triggers</span>
                                <ul className="mt-1 space-y-1">
                                  {segment.decision_psychology.decision_triggers.map((trigger, tidx) => (
                                    <li key={tidx} className="text-sm text-gray-300 flex items-start gap-2">
                                      <Zap className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                      {trigger}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {segment.decision_psychology.trust_builders && (
                              <div className="mt-3">
                                <span className="text-xs text-gray-400">Trust Builders</span>
                                <ul className="mt-1 space-y-1">
                                  {segment.decision_psychology.trust_builders.map((trust, tidx) => (
                                    <li key={tidx} className="text-sm text-gray-300 flex items-start gap-2">
                                      <Shield className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                      {trust}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Platform Behavior */}
                        {segment.platform_behavior && (
                          <div className="border-t border-gray-700 pt-4 mt-4">
                            <h4 className="text-sm font-semibold text-blue-400 mb-3">Platform Behavior</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {segment.platform_behavior.facebook_instagram && (
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">📘</span>
                                    <span className="text-sm font-medium text-white">Facebook/Instagram</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mb-2">{segment.platform_behavior.facebook_instagram.usage_pattern}</p>
                                  <div className="text-xs text-gray-300">
                                    <span className="text-blue-400">Peak:</span> {segment.platform_behavior.facebook_instagram.peak_times}
                                  </div>
                                </div>
                              )}
                              
                              {segment.platform_behavior.google && (
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">🔍</span>
                                    <span className="text-sm font-medium text-white">Google</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mb-2">{segment.platform_behavior.google.search_intent}</p>
                                  <div className="text-xs text-gray-300">
                                    <span className="text-blue-400">Queries:</span> {segment.platform_behavior.google.query_patterns?.slice(0, 2).join(', ')}
                                  </div>
                                </div>
                              )}
                              
                              {segment.platform_behavior.regional_platforms && (
                                <div className="bg-gray-900/50 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">📱</span>
                                    <span className="text-sm font-medium text-white">{segment.platform_behavior.regional_platforms.platform_name}</span>
                                  </div>
                                  <p className="text-xs text-gray-400">{segment.platform_behavior.regional_platforms['behavior on local platforms']}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Spending Behavior */}
                        {segment.spending_behavior && (
                          <div className="border-t border-gray-700 pt-4 mt-4">
                            <h4 className="text-sm font-semibold text-emerald-400 mb-3">Spending Behavior</h4>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <span className="text-xs text-gray-400">Avg Transaction</span>
                                <p className="text-sm font-semibold text-white">{segment.spending_behavior.average_transaction}</p>
                              </div>
                              <div>
                                <span className="text-xs text-gray-400">Frequency</span>
                                <p className="text-sm font-semibold text-white">{segment.spending_behavior.purchase_frequency}</p>
                              </div>
                              <div>
                                <span className="text-xs text-gray-400">Payment Prefs</span>
                                <p className="text-sm font-semibold text-white">{segment.spending_behavior.payment_preferences}</p>
                              </div>
                              <div>
                                <span className="text-xs text-gray-400">Deal Seeking</span>
                                <p className="text-sm font-semibold text-white">{segment.spending_behavior.deal_seeking}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

             {/* Emotional Journey Map */}
            {recommendations.audiencePsychology.detailed_analysis?.emotional_journey_map && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-orange-500" />
                  Emotional Journey Map
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Awareness Stage */}
                  {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness && (
                    <div className="rounded-lg p-4 bg-purple-500/10 border border-purple-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-purple-500">
                        <span className="text-white text-sm font-bold">1</span>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-white mb-3">Awareness</h3>
                      
                      <div className="space-y-3">
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.emotions?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Emotions:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.emotions.map((emotion, eidx) => (
                                <span key={eidx} className="text-xs bg-white/10 text-white px-2 py-0.5 rounded">
                                  {emotion}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.thoughts?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Thoughts:</span>
                            <p className="text-xs text-gray-300 italic mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.thoughts[0]}
                            </p>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.actions?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Actions:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.actions.map((action, aidx) => (
                                <li key={aidx} className="text-xs text-gray-300">• {action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.cultural_triggers && (
                          <div>
                            <span className="text-xs text-purple-400">Cultural Trigger:</span>
                            <p className="text-xs text-gray-300 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.awareness.cultural_triggers}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Consideration Stage */}
                  {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration && (
                    <div className="rounded-lg p-4 bg-blue-500/10 border border-blue-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-blue-500">
                        <span className="text-white text-sm font-bold">2</span>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-white mb-3">Consideration</h3>
                      
                      <div className="space-y-3">
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.emotions?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Emotions:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.emotions.map((emotion, eidx) => (
                                <span key={eidx} className="text-xs bg-white/10 text-white px-2 py-0.5 rounded">
                                  {emotion}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.comparison_behavior && (
                          <div>
                            <span className="text-xs text-gray-400">Comparison Behavior:</span>
                            <p className="text-xs text-gray-300 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.comparison_behavior}
                            </p>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.information_needs?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Information Needs:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.information_needs.slice(0, 3).map((need, nidx) => (
                                <li key={nidx} className="text-xs text-gray-300">• {need}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.trusted_sources && (
                          <div>
                            <span className="text-xs text-blue-400">Trusted Sources:</span>
                            <p className="text-xs text-gray-300 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.consideration.trusted_sources}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Decision Stage */}
                  {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision && (
                    <div className="rounded-lg p-4 bg-emerald-500/10 border border-emerald-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-emerald-500">
                        <span className="text-white text-sm font-bold">3</span>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-white mb-3">Decision</h3>
                      
                      <div className="space-y-3">
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.final_triggers?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Final Triggers:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.final_triggers.map((trigger, tidx) => (
                                <li key={tidx} className="text-xs text-gray-300">• {trigger}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.risk_mitigation?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Risk Mitigation:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.risk_mitigation.map((risk, ridx) => (
                                <li key={ridx} className="text-xs text-gray-300">• {risk}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.social_validation && (
                          <div>
                            <span className="text-xs text-emerald-400">Social Validation:</span>
                            <p className="text-xs text-gray-300 mt-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.social_validation}
                            </p>
                          </div>
                        )}
                        
                        {/* Fallback if decision stage has no data */}
                        {!recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.final_triggers?.length &&
                        !recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.risk_mitigation?.length &&
                        !recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision.social_validation && (
                          <div className="text-xs text-gray-400 italic">
                            <p className="mb-2">Key decision factors:</p>
                            <ul className="space-y-1">
                              <li>• Compelling offer or promotion</li>
                              <li>• Trust signals confirmed</li>
                              <li>• Peer recommendations</li>
                              <li>• Easy onboarding process</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Post Purchase Stage */}
                  {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase && (
                    <div className="rounded-lg p-4 bg-orange-500/10 border border-orange-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-orange-500">
                        <span className="text-white text-sm font-bold">4</span>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-white mb-3">Post Purchase</h3>
                      
                      <div className="space-y-3">
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.satisfaction_drivers?.length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Satisfaction Drivers:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.satisfaction_drivers.map((driver, didx) => (
                                <li key={didx} className="text-xs text-gray-300">• {driver}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.advocacy_triggers?.length > 0 && (
                          <div>
                            <span className="text-xs text-orange-400">Advocacy Triggers:</span>
                            <ul className="mt-1 space-y-1">
                              {recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.advocacy_triggers.map((trigger, tidx) => (
                                <li key={tidx} className="text-xs text-gray-300">• {trigger}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Fallback if post_purchase stage has no data */}
                        {!recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.satisfaction_drivers?.length &&
                        !recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase.advocacy_triggers?.length && (
                          <div className="text-xs text-gray-400 italic">
                            <p className="mb-2">Post-purchase goals:</p>
                            <ul className="space-y-1">
                              <li>• Successful onboarding</li>
                              <li>• First transaction completed</li>
                              <li>• Positive experience shared</li>
                              <li>• Referral program activation</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Fallback for missing stages - create placeholder cards */}
                  {!recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.decision && (
                    <div className="rounded-lg p-4 bg-emerald-500/10 border border-emerald-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-emerald-500">
                        <span className="text-white text-sm font-bold">3</span>
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-3">Decision</h3>
                      <div className="text-xs text-gray-400 italic">
                        <p className="mb-2">Key decision factors:</p>
                        <ul className="space-y-1">
                          <li>• Compelling offer or promotion</li>
                          <li>• Trust signals confirmed</li>
                          <li>• Peer recommendations</li>
                          <li>• Easy onboarding process</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {!recommendations.audiencePsychology.detailed_analysis.emotional_journey_map.post_purchase && (
                    <div className="rounded-lg p-4 bg-orange-500/10 border border-orange-500/30">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 bg-orange-500">
                        <span className="text-white text-sm font-bold">4</span>
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-3">Post Purchase</h3>
                      <div className="text-xs text-gray-400 italic">
                        <p className="mb-2">Post-purchase goals:</p>
                        <ul className="space-y-1">
                          <li>• Successful onboarding</li>
                          <li>• First transaction completed</li>
                          <li>• Positive experience shared</li>
                          <li>• Referral program activation</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

              {/* Cultural Factors */}
              {recommendations.audiencePsychology.cultural_considerations && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-purple-500" />
                    Cultural Factors - {recommendations.country}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-purple-400 mb-2">Communication Style</h3>
                      <p className="text-sm text-gray-300">{recommendations.audiencePsychology.cultural_considerations.communication_style}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-blue-400 mb-2">Decision Making</h3>
                      <p className="text-sm text-gray-300">{recommendations.audiencePsychology.cultural_considerations.decision_making}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-emerald-400 mb-2">Brand Loyalty</h3>
                      <p className="text-sm text-gray-300">{recommendations.audiencePsychology.cultural_considerations.brand_loyalty}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-orange-400 mb-2">Social Proof Importance</h3>
                      <p className="text-sm text-gray-300">{recommendations.audiencePsychology.cultural_considerations.social_proof_importance}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4 md:col-span-2">
                      <h3 className="text-sm font-semibold text-yellow-400 mb-2">Local Influencers</h3>
                      <p className="text-sm text-gray-300">{recommendations.audiencePsychology.cultural_considerations.local_influencers}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Messaging Framework */}
              {recommendations.audiencePsychology.messaging_framework && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-500" />
                    Messaging Framework
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Power Words */}
                    {recommendations.audiencePsychology.messaging_framework.power_words && (
                      <div>
                        <h3 className="text-sm font-semibold text-emerald-400 mb-3">Power Words to Use</h3>
                        <div className="flex flex-wrap gap-2">
                          {recommendations.audiencePsychology.messaging_framework.power_words.map((word, idx) => (
                            <span key={idx} className="text-sm bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full">
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Content Themes */}
                    {recommendations.audiencePsychology.messaging_framework.content_themes && (
                      <div>
                        <h3 className="text-sm font-semibold text-blue-400 mb-3">Content Themes</h3>
                        <ul className="space-y-2">
                          {recommendations.audiencePsychology.messaging_framework.content_themes.map((theme, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                              <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              {theme}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Emotional Hooks */}
                  {recommendations.audiencePsychology.messaging_framework.emotional_hooks && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-orange-400 mb-3">Emotional Hooks</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recommendations.audiencePsychology.messaging_framework.emotional_hooks.map((hook, idx) => (
                          <div key={idx} className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">💡</span>
                              <span className="text-sm font-semibold text-orange-400">{hook.emotion}</span>
                            </div>
                            <p className="text-sm text-gray-300 mb-2">{hook.message_angle}</p>
                            <p className="text-xs text-gray-400">
                              <span className="text-purple-400">Visual Style:</span> {hook.visual_style}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Social Proof Types */}
                  {recommendations.audiencePsychology.messaging_framework.social_proof_types && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-purple-400 mb-3">Social Proof Types</h3>
                      <div className="flex flex-wrap gap-2">
                        {recommendations.audiencePsychology.messaging_framework.social_proof_types.map((proof, idx) => (
                          <span key={idx} className="text-sm bg-purple-500/20 text-purple-300 px-3 py-1 rounded-lg flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            {proof}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Content Preferences */}
              {recommendations.audiencePsychology.detailed_analysis?.content_preferences && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    Content Preferences
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Format Preferences */}
                    {recommendations.audiencePsychology.detailed_analysis.content_preferences.formats && (
                      <div>
                        <h3 className="text-sm font-semibold text-blue-400 mb-3">Format Preferences</h3>
                        <div className="space-y-3">
                          {recommendations.audiencePsychology.detailed_analysis.content_preferences.formats.map((format, idx) => (
                            <div key={idx} className="bg-gray-800/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-white capitalize">{format.format}</span>
                                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                                  {format.preference_score}% preference
                                </span>
                              </div>
                              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${format.preference_score}%` }}
                                />
                              </div>
                              <p className="text-xs text-gray-400 mt-2">{format.best_use_case}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Style & Tone */}
                    <div className="space-y-4">
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-purple-400 mb-2">Visual Style</h3>
                        <p className="text-sm text-gray-300">
                          {recommendations.audiencePsychology.detailed_analysis.content_preferences.visual_style}
                        </p>
                      </div>
                      
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-emerald-400 mb-2">Tone of Voice</h3>
                        <p className="text-sm text-gray-300">
                          {recommendations.audiencePsychology.detailed_analysis.content_preferences.tone_of_voice}
                        </p>
                      </div>
                      
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-orange-400 mb-2">Language Preferences</h3>
                        <p className="text-sm text-gray-300">
                          {recommendations.audiencePsychology.detailed_analysis.content_preferences.language_preferences}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Audience Analysis Not Available</h3>
              <p className="text-sm text-gray-400">
                Audience psychology data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}

       {/* Journey Tab */}
      {activeTab === 'journey' && (
        <div className="space-y-6">
          {recommendations.customerJourneyMap ? (
            <>
              {/* Journey Stages Overview */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-purple-500" />
                  Customer Journey Map
                </h2>
                
                {/* Visual Journey Flow */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                  {recommendations.customerJourneyMap.stages?.map((stage, idx) => (
                    <div key={idx} className="flex-1 relative">
                      <div className="flex flex-row md:flex-col items-center md:items-center gap-4 md:gap-0">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          idx === 0 ? 'bg-purple-500' : 
                          idx === 1 ? 'bg-blue-500' : 
                          idx === 2 ? 'bg-emerald-500' : 
                          'bg-orange-500'
                        }`}>
                          {idx === 0 ? <Eye className="w-6 h-6 text-white" /> :
                          idx === 1 ? <Search className="w-6 h-6 text-white" /> :
                          idx === 2 ? <Target className="w-6 h-6 text-white" /> :
                          <Award className="w-6 h-6 text-white" />}
                        </div>
                        <div className="text-left md:text-center md:mt-3">
                          <span className="text-sm font-semibold text-white">{stage.stage}</span>
                          <p className="text-xs text-gray-400">{stage.duration}</p>
                        </div>
                      </div>
                      {idx < recommendations.customerJourneyMap.stages.length - 1 && (
                        <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-gray-600 -translate-x-1/2">
                          <ChevronRight className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Detailed Stage Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendations.customerJourneyMap.stages?.map((stage, idx) => (
                  <div key={idx} className={`rounded-xl p-4 md:p-6 border ${
                    idx === 0 ? 'bg-purple-500/10 border-purple-500/30' : 
                    idx === 1 ? 'bg-blue-500/10 border-blue-500/30' : 
                    idx === 2 ? 'bg-emerald-500/10 border-emerald-500/30' : 
                    'bg-orange-500/10 border-orange-500/30'
                  }`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-purple-500' : 
                        idx === 1 ? 'bg-blue-500' : 
                        idx === 2 ? 'bg-emerald-500' : 
                        'bg-orange-500'
                      }`}>
                        <span className="text-white font-bold">{idx + 1}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{stage.stage}</h3>
                        <p className="text-xs text-gray-400">{stage.duration} • {stage.customer_mindset}</p>
                      </div>
                    </div>
                    
                    {/* Platforms for this stage */}
                    {stage.platforms && stage.platforms.length > 0 && (
                      <div className="mb-4">
                        <span className="text-xs text-gray-400">Platforms</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {stage.platforms.map((platform, pidx) => (
                            <span key={pidx} className="text-xs bg-white/10 px-3 py-1 rounded-full flex items-center gap-1">
                              {getPlatformIcon(platform)} {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Touchpoints */}
                    {stage.touchpoints && (
                      <div className="mb-4">
                        <span className="text-xs text-gray-400">Touchpoints</span>
                        <ul className="mt-2 space-y-1">
                          {stage.touchpoints.map((touchpoint, tidx) => (
                            <li key={tidx} className="text-sm text-gray-300 flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                              {touchpoint}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Content Types */}
                    {stage.content_types && (
                      <div className="mb-4">
                        <span className="text-xs text-gray-400">Content Types</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {stage.content_types.map((content, cidx) => (
                            <span key={cidx} className="text-xs bg-white/5 text-gray-300 px-2 py-1 rounded">
                              {content}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Metrics */}
                    {stage.metrics && (
                      <div className="mb-4">
                        <span className="text-xs text-gray-400">Key Metrics</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {stage.metrics.map((metric, midx) => (
                            <span key={midx} className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded">
                              {metric}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Regional Considerations */}
                    {stage.regional_considerations && (
                      <div className="mt-4 p-3 bg-white/5 rounded-lg">
                        <span className="text-xs text-purple-400">Regional Consideration:</span>
                        <p className="text-sm text-gray-300 mt-1">{stage.regional_considerations}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Platform Roles */}
              {recommendations.customerJourneyMap.platform_roles && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-500" />
                    Platform Roles in Journey
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recommendations.customerJourneyMap.platform_roles.map((role, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">{getPlatformIcon(role.platform)}</span>
                          <span className="text-white font-semibold">{role.platform}</span>
                        </div>
                        
                        <div className="mb-3">
                          <span className="text-xs text-gray-400">Primary Stages:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {role.primary_stages?.map((stage, sidx) => (
                              <span key={sidx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded capitalize">
                                {stage}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {role.key_tactics && (
                          <div>
                            <span className="text-xs text-gray-400">Key Tactics:</span>
                            <ul className="mt-1 space-y-1">
                              {role.key_tactics.map((tactic, tidx) => (
                                <li key={tidx} className="text-xs text-gray-300">{tactic}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Optimization Opportunities */}
              {recommendations.customerJourneyMap.optimization_opportunities && (
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Optimization Opportunities
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recommendations.customerJourneyMap.optimization_opportunities.map((opp, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                        <Lightbulb className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{opp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Navigation className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Customer Journey Not Available</h3>
              <p className="text-sm text-gray-400">
                Journey mapping data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Roadmap Tab */}
      {activeTab === 'roadmap' && (
        <div className="space-y-6">
          {recommendations.strategicRoadmap ? (
            <>
              {/* Roadmap Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <Map className="w-5 h-5 text-purple-500" />
                  {recommendations.strategicRoadmap.total_duration} Strategic Roadmap
                </h2>
                <p className="text-gray-300">{recommendations.strategicRoadmap.summary}</p>
              </div>

              {/* Implementation Phases */}
              {recommendations.strategicRoadmap.implementation_phases && (
                <div className="space-y-4">
                  {Object.entries(recommendations.strategicRoadmap.implementation_phases).map(([phaseKey, phase], idx) => (
                    <div key={phaseKey} className={`rounded-xl p-4 md:p-6 border ${
                      idx === 0 ? 'bg-purple-500/10 border-purple-500/30' :
                      idx === 1 ? 'bg-blue-500/10 border-blue-500/30' :
                      idx === 2 ? 'bg-emerald-500/10 border-emerald-500/30' :
                      'bg-orange-500/10 border-orange-500/30'
                    }`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            idx === 0 ? 'bg-purple-500' :
                            idx === 1 ? 'bg-blue-500' :
                            idx === 2 ? 'bg-emerald-500' :
                            'bg-orange-500'
                          }`}>
                            <span className="text-white font-bold">{idx + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{phase.name}</h3>
                            <p className="text-sm text-gray-400">{phase.duration}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-white">{phase.budget_display}</span>
                          <p className="text-xs text-gray-400">Budget Deployment</p>
                        </div>
                      </div>
                      
                      {/* Platforms to Launch */}
                      {phase.platforms_to_launch && (
                        <div className="mb-4">
                          <span className="text-xs text-gray-400">Platforms:</span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(Array.isArray(phase.platforms_to_launch) ? phase.platforms_to_launch : [phase.platforms_to_launch]).map((platform, pidx) => (
                              <span key={pidx} className="text-sm bg-white/10 px-3 py-1 rounded-full flex items-center gap-1">
                                {getPlatformIcon(platform)} {platform}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Key Activities */}
                      {phase.key_activities && (
                        <div className="mb-4">
                          <span className="text-xs text-gray-400">Key Activities:</span>
                          <ul className="mt-2 space-y-2">
                            {phase.key_activities.map((activity, aidx) => (
                              <li key={aidx} className="text-sm text-gray-300 flex items-start gap-2">
                                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {activity}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Milestones */}
                      {phase.milestones && (
                        <div className="mb-4">
                          <span className="text-xs text-gray-400">Milestones:</span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                            {phase.milestones.map((milestone, midx) => (
                              <div key={midx} className="bg-white/5 rounded-lg p-3 text-center">
                                <span className="text-2xl">{milestone.icon}</span>
                                <p className="text-xs text-gray-400 mt-1">Day {milestone.day}</p>
                                <p className="text-xs text-white mt-1">{milestone.milestone}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Success Metrics */}
                      {phase.success_metrics && (
                        <div className="p-3 bg-white/5 rounded-lg">
                          <span className="text-xs text-emerald-400">Success Metrics:</span>
                          <ul className="mt-2 space-y-1">
                            {phase.success_metrics.map((metric, midx) => (
                              <li key={midx} className="text-sm text-gray-300 flex items-start gap-2">
                                <Target className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {metric}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* KPI Targets */}
              {recommendations.strategicRoadmap.kpi_targets && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-emerald-500" />
                    KPI Targets by Week
                  </h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(recommendations.strategicRoadmap.kpi_targets).map(([week, targets], idx) => (
                      <div key={week} className="bg-gray-800/50 rounded-lg p-4 text-center">
                        <span className="text-sm text-gray-400 capitalize">{week.replace('_', ' ')}</span>
                        <div className="mt-2">
                          <span className="text-2xl font-bold text-emerald-400">{targets.roas}x</span>
                          <p className="text-xs text-gray-400">Target ROAS</p>
                        </div>
                        <div className="mt-2">
                          <span className="text-lg font-semibold text-blue-400">{targets.cpa_index}%</span>
                          <p className="text-xs text-gray-400">CPA Index</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contingency Plans */}
              {recommendations.strategicRoadmap.contingency_plans && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-yellow-500" />
                    Contingency Plans
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(recommendations.strategicRoadmap.contingency_plans).map(([planKey, plan], idx) => (
                      <div key={planKey} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-yellow-400 capitalize mb-2">
                          {planKey.replace('_', ' ')}
                        </h3>
                        <div className="mb-2">
                          <span className="text-xs text-gray-400">Trigger:</span>
                          <p className="text-sm text-white">{plan.trigger}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Action:</span>
                          <p className="text-sm text-gray-300">{plan.action}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Map className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Strategic Roadmap Not Available</h3>
              <p className="text-sm text-gray-400">
                Roadmap data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Competitive Tab */}
      {activeTab === 'competitive' && (
        <div className="space-y-6">
          {recommendations.competitiveAnalysis ? (
            <>
             {/* Competitive Summary */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Swords className="w-5 h-5 text-red-500" />
              Competitive Analysis
            </h2>
            <p className="text-gray-300">{recommendations.competitiveAnalysis.summary}</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <div className="bg-emerald-500/20 rounded-lg px-4 py-3">
                <span className="text-xl sm:text-2xl font-bold text-emerald-400 block">
                  {recommendations.competitiveAnalysis.detailed_analysis?.market_share_potential?.current_estimate || '0%'}
                </span>
                <p className="text-xs text-gray-400 mt-1">Current Market Share</p>
              </div>
              <div className="bg-blue-500/20 rounded-lg px-4 py-3">
                <span className="text-xl sm:text-2xl font-bold text-blue-400 block break-words">
                  {recommendations.competitiveAnalysis.detailed_analysis?.market_share_potential?.achievable_12_months || 'N/A'}
                </span>
                <p className="text-xs text-gray-400 mt-1">12-Month Target</p>
              </div>
              <div className="bg-purple-500/20 rounded-lg px-4 py-3">
                <span className="text-xl sm:text-2xl font-bold text-purple-400 block">
                  {recommendations.competitiveAnalysis.detailed_analysis?.win_probability || 0}%
                </span>
                <p className="text-xs text-gray-400 mt-1">Win Probability</p>
              </div>
            </div>
          </div>

              {/* Direct Competitors */}
              {recommendations.competitiveAnalysis.detailed_analysis?.market_positioning?.direct_competitors && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-500" />
                    Direct Competitors
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recommendations.competitiveAnalysis.detailed_analysis.market_positioning.direct_competitors.map((competitor, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-white font-semibold">{competitor.competitor_type}</span>
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                            {competitor.market_share} share
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Est. Ad Spend:</span>
                            <span className="text-white">{competitor.estimated_ad_spend}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Primary Platforms:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {competitor.primary_platforms?.map((platform, pidx) => (
                                <span key={pidx} className="text-xs bg-white/10 px-2 py-0.5 rounded">
                                  {platform}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs text-emerald-400">Weakness to Exploit:</span>
                            <p className="text-xs text-gray-300">{competitor.weakness_to_exploit}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Competitive Advantages */}
              {recommendations.competitiveAnalysis.detailed_analysis?.competitive_advantages && (
                <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-emerald-500" />
                    Your Competitive Advantages
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendations.competitiveAnalysis.detailed_analysis.competitive_advantages.map((advantage, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-2">{advantage.advantage}</h3>
                        <p className="text-sm text-gray-300 mb-2">{advantage.messaging_angle}</p>
                        <div className="flex flex-wrap gap-2">
                          {advantage.platforms_where_strongest?.map((platform, pidx) => (
                            <span key={pidx} className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded">
                              {platform}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-purple-400 mt-2">Cultural Fit: {advantage.cultural_fit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Market Gaps */}
              {recommendations.competitiveAnalysis.detailed_analysis?.market_positioning?.market_gaps && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Market Gaps & Opportunities
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendations.competitiveAnalysis.detailed_analysis.market_positioning.market_gaps.map((gap, idx) => (
                      <div key={idx} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold">{gap.gap}</h3>
                          <span className={`text-xs px-2 py-1 rounded ${
                            gap.opportunity_size === 'high' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {gap.opportunity_size} opportunity
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">Est. Value: {gap.estimated_value}</p>
                        <div className="flex flex-wrap gap-1">
                          {gap.platforms_to_target?.map((platform, pidx) => (
                            <span key={pidx} className="text-xs bg-white/10 text-gray-300 px-2 py-0.5 rounded">
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blue Ocean Opportunities */}
              {recommendations.competitiveAnalysis.detailed_analysis?.blue_ocean_opportunities && (
                <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-500" />
                    Blue Ocean Opportunities
                  </h2>
                  
                  <div className="space-y-4">
                    {recommendations.competitiveAnalysis.detailed_analysis.blue_ocean_opportunities.map((opp, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold">{opp.opportunity}</h3>
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                            {opp.first_mover_advantage} first-mover advantage
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">Investment: {opp.investment_required}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {opp.platforms?.map((platform, pidx) => (
                            <span key={pidx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Differentiation Strategies */}
              {recommendations.competitiveAnalysis.detailed_analysis?.differentiation_strategies && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-500" />
                    Differentiation Strategies
                  </h2>
                  
                  <div className="space-y-4">
                    {recommendations.competitiveAnalysis.detailed_analysis.differentiation_strategies.map((strategy, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold">{strategy.strategy}</h3>
                          <span className={`text-xs px-2 py-1 rounded ${
                            strategy.implementation_difficulty === 'medium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : strategy.implementation_difficulty === 'easy'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-red-500/20 text-red-400'
                          }`}>
                            {strategy.implementation_difficulty} difficulty
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">{strategy.expected_advantage}</p>
                        <p className="text-xs text-purple-400">Cultural Alignment: {strategy.cultural_alignment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Swords className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Competitive Analysis Not Available</h3>
              <p className="text-sm text-gray-400">
                Competitive analysis data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Predictions Tab */}
{activeTab === 'predictions' && (
  <div className="space-y-6">
    {recommendations.performancePredictions ? (
      <>
        {/* Predictions Summary */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Performance Predictions
          </h2>
          <p className="text-gray-300">{recommendations.performancePredictions.summary}</p>
          
          {/* Prediction Confidence */}
          {recommendations.performancePredictions.prediction_confidence && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <span className="text-xl font-bold text-blue-400">
                  {recommendations.performancePredictions.prediction_confidence.market_data_quality}%
                </span>
                <p className="text-xs text-gray-400">Market Data Quality</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <span className="text-xl font-bold text-purple-400">
                  {recommendations.performancePredictions.prediction_confidence.historical_accuracy}%
                </span>
                <p className="text-xs text-gray-400">Historical Accuracy</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <span className="text-xl font-bold text-emerald-400">
                  {recommendations.performancePredictions.prediction_confidence.model_confidence}%
                </span>
                <p className="text-xs text-gray-400">Model Confidence</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <span className="text-xl font-bold text-orange-400">
                  {recommendations.performancePredictions.prediction_confidence.overall}%
                </span>
                <p className="text-xs text-gray-400">Overall Confidence</p>
              </div>
            </div>
          )}
        </div>

        {/* Monthly Predictions */}
        {recommendations.performancePredictions.monthly_predictions && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Monthly Performance Projections
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(recommendations.performancePredictions.monthly_predictions).map(([monthKey, month], idx) => (
                <div key={monthKey} className={`rounded-xl p-4 border ${
                  idx === 0 ? 'bg-purple-500/10 border-purple-500/30' :
                  idx === 1 ? 'bg-blue-500/10 border-blue-500/30' :
                  'bg-emerald-500/10 border-emerald-500/30'
                }`}>
                  <h3 className="text-lg font-semibold text-white mb-3 capitalize">
                    {monthKey.replace('_', ' ')}
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Budget:</span>
                      <span className="text-lg font-bold text-white">{month.budget_display}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Expected ROAS:</span>
                      <span className="text-lg font-bold text-emerald-400">{month.expected_roas}x</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Platforms Active:</span>
                      <span className="text-sm text-white">{month.platforms_active}</span>
                    </div>
                    
                    <div className="text-xs text-gray-400">
                      Confidence Range: {month.confidence_range?.low}x - {month.confidence_range?.high}x
                    </div>
                    
                    <div className="pt-3 border-t border-white/10">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <span className="text-sm font-semibold text-white">
                            {(month.estimated_impressions / 1000000).toFixed(1)}M
                          </span>
                          <p className="text-xs text-gray-400">Impressions</p>
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-white">
                            {(month.estimated_clicks / 1000).toFixed(0)}K
                          </span>
                          <p className="text-xs text-gray-400">Clicks</p>
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-emerald-400">
                            {month.estimated_conversions?.toLocaleString()}
                          </span>
                          <p className="text-xs text-gray-400">Conversions</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-white/10">
                      <span className="text-xs text-gray-400">Focus:</span>
                      <p className="text-sm text-gray-300 mt-1">{month.key_focus}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quarterly Projection */}
        {recommendations.performancePredictions.quarterly_projection && (
          <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-xl p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              Quarterly Projection
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Q1 Total */}
              {recommendations.performancePredictions.quarterly_projection.q1_total && (
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-emerald-400 mb-3">Q1 Total</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-400">Total Spend</span>
                      <p className="text-lg font-bold text-white">
                        {recommendations.performancePredictions.quarterly_projection.q1_total.total_spend_display}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Total Revenue</span>
                      <p className="text-lg font-bold text-emerald-400">
                        {recommendations.performancePredictions.quarterly_projection.q1_total.total_revenue_display}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Total Conversions</span>
                      <p className="text-lg font-bold text-white">
                        {recommendations.performancePredictions.quarterly_projection.q1_total.total_conversions?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Average ROAS</span>
                      <p className="text-lg font-bold text-blue-400">
                        {recommendations.performancePredictions.quarterly_projection.q1_total.average_roas}x
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Q2 Projection */}
              {recommendations.performancePredictions.quarterly_projection.q2_projection && (
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-400 mb-3">Q2 Projection</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Recommended Budget:</span>
                      <span className="text-sm font-semibold text-white">{recommendations.performancePredictions.quarterly_projection.q2_projection.recommended_budget_display}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Expected ROAS:</span>
                      <span className="text-sm font-semibold text-emerald-400">
                        {recommendations.performancePredictions.quarterly_projection.q2_projection.expected_roas}x
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">Market Share Gain:</span>
                      <span className="text-sm font-semibold text-purple-400">
                        {recommendations.performancePredictions.quarterly_projection.q2_projection.market_share_gain}
                      </span>
                    </div>
                    {recommendations.performancePredictions.quarterly_projection.q2_projection.expansion_opportunities && (
                      <div className="pt-3 border-t border-white/10">
                        <span className="text-xs text-gray-400">Expansion Opportunities:</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {recommendations.performancePredictions.quarterly_projection.q2_projection.expansion_opportunities.map((opp, idx) => (
                            <span key={idx} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                              {opp}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scenarios */}
        {recommendations.performancePredictions.scenarios && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Sliders className="w-5 h-5 text-purple-500" />
              Performance Scenarios
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Conservative */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-white font-semibold">Conservative</h3>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  {recommendations.performancePredictions.scenarios.conservative?.description}
                </p>
                <div className="text-2xl font-bold text-yellow-400">
                  {recommendations.performancePredictions.scenarios.conservative?.month_3_roas}x ROAS
                </div>
              </div>
              
              {/* Expected */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-emerald-500" />
                  <h3 className="text-white font-semibold">Expected</h3>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  {recommendations.performancePredictions.scenarios.expected?.description}
                </p>
                <div className="text-2xl font-bold text-emerald-400">
                  {recommendations.performancePredictions.scenarios.expected?.month_3_roas}x ROAS
                </div>
              </div>
              
              {/* Optimistic */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  <h3 className="text-white font-semibold">Optimistic</h3>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  {recommendations.performancePredictions.scenarios.optimistic?.description}
                </p>
                <div className="text-2xl font-bold text-blue-400">
                  {recommendations.performancePredictions.scenarios.optimistic?.month_3_roas}x ROAS
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Key Assumptions */}
        {recommendations.performancePredictions.key_assumptions && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-gray-500" />
              Key Assumptions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendations.performancePredictions.key_assumptions.map((assumption, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-3">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{assumption}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    ) : (
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
        <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Performance Predictions Not Available</h3>
        <p className="text-sm text-gray-400">
          Performance prediction data wasn't generated for this analysis.
        </p>
      </div>
    )}
  </div>
)}

       {/* Risks Tab */}
      {activeTab === 'risks' && (
        <div className="space-y-6">
          {recommendations.riskAssessment ? (
            <>
              {/* Risk Overview */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-500" />
                  Risk Assessment
                </h2>
                
                <p className="text-gray-300 mb-4">{recommendations.riskAssessment.summary}</p>
                
                <div className="flex items-center gap-4">
                  <div className={`px-4 py-2 rounded-lg ${
                    recommendations.riskAssessment.overall_risk_level === 'low' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : recommendations.riskAssessment.overall_risk_level === 'medium'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                  }`}>
                    <span className="text-lg font-bold capitalize">
                      {recommendations.riskAssessment.overall_risk_level} Risk
                    </span>
                  </div>
                  <div className="bg-gray-800/50 px-4 py-2 rounded-lg">
                    <span className="text-lg font-bold text-white">
                      {recommendations.riskAssessment.overall_risk_score}/100
                    </span>
                    <span className="text-xs text-gray-400 ml-2">Risk Score</span>
                  </div>
                </div>
              </div>

              {/* Identified Risks */}
              {recommendations.riskAssessment.identified_risks && recommendations.riskAssessment.identified_risks.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    Identified Risks
                  </h2>
                  
                  <div className="space-y-4">
                    {recommendations.riskAssessment.identified_risks.map((risk, idx) => (
                      <div key={idx} className={`rounded-lg p-4 border ${
                        risk.severity === 'high' 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : risk.severity === 'medium'
                            ? 'bg-yellow-500/10 border-yellow-500/30'
                            : 'bg-emerald-500/10 border-emerald-500/30'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              risk.severity === 'high' ? 'bg-red-500' :
                              risk.severity === 'medium' ? 'bg-yellow-500' : 'bg-emerald-500'
                            }`} />
                            <div>
                              <h3 className="text-white font-semibold">{risk.description}</h3>
                              <span className="text-xs text-gray-400 capitalize">{risk.category} • {risk.risk_type}</span>
                            </div>
                          </div>
                          <span className={`text-sm font-semibold px-3 py-1 rounded ${
                            risk.severity === 'high' 
                              ? 'bg-red-500/20 text-red-400' 
                              : risk.severity === 'medium'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {risk.probability}% likely
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-300 mb-3">{risk.impact}</p>
                        
                        {risk.mitigation_strategies && risk.mitigation_strategies.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <span className="text-xs text-purple-400 font-semibold">Mitigation Strategies:</span>
                            <ul className="mt-2 space-y-1">
                              {risk.mitigation_strategies.map((strategy, sidx) => (
                                <li key={sidx} className="text-sm text-gray-300 flex items-start gap-2">
                                  <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                                  {strategy}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {risk.regional_factors && (
                          <div className="mt-3 p-2 bg-white/5 rounded">
                            <span className="text-xs text-blue-400">Regional Factor:</span>
                            <span className="text-xs text-gray-300 ml-2">{risk.regional_factors}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Matrix */}
              {recommendations.riskAssessment.risk_matrix && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-500" />
                    Risk Matrix
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* High Severity */}
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        High Severity ({recommendations.riskAssessment.risk_matrix.high_severity?.length || 0})
                      </h3>
                      {recommendations.riskAssessment.risk_matrix.high_severity?.length > 0 ? (
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.risk_matrix.high_severity.map((risk, idx) => (
                            <li key={idx} className="text-sm text-gray-300">{risk.description}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No high severity risks</p>
                      )}
                    </div>
                    
                    {/* Medium Severity */}
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <h3 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Medium Severity ({recommendations.riskAssessment.risk_matrix.medium_severity?.length || 0})
                      </h3>
                      {recommendations.riskAssessment.risk_matrix.medium_severity?.length > 0 ? (
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.risk_matrix.medium_severity.map((risk, idx) => (
                            <li key={idx} className="text-sm text-gray-300">{risk.description}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No medium severity risks</p>
                      )}
                    </div>
                    
                    {/* Low Severity */}
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                      <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Low Severity ({recommendations.riskAssessment.risk_matrix.low_severity?.length || 0})
                      </h3>
                      {recommendations.riskAssessment.risk_matrix.low_severity?.length > 0 ? (
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.risk_matrix.low_severity.map((risk, idx) => (
                            <li key={idx} className="text-sm text-gray-300">{risk.description}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No low severity risks</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mitigation Priorities */}
              {recommendations.riskAssessment.mitigation_priorities && recommendations.riskAssessment.mitigation_priorities.length > 0 && (
                <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-500" />
                    Mitigation Priorities
                  </h2>
                  
                  <div className="space-y-4">
                    {recommendations.riskAssessment.mitigation_priorities.map((priority, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded font-semibold">
                            {priority.priority}
                          </span>
                          <span className="text-white font-semibold capitalize">{priority.risk.replace('_', ' ')}</span>
                        </div>
                        <ul className="space-y-2">
                          {priority.actions?.map((action, aidx) => (
                            <li key={aidx} className="text-sm text-gray-300 flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monitoring Framework */}
              {recommendations.riskAssessment.monitoring_framework && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-500" />
                    Monitoring Framework
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Daily Checks */}
                    {recommendations.riskAssessment.monitoring_framework.daily_checks && (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Daily Checks
                        </h3>
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.monitoring_framework.daily_checks.map((check, idx) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                              <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              {check}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Weekly Reviews */}
                    {recommendations.riskAssessment.monitoring_framework.weekly_reviews && (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Weekly Reviews
                        </h3>
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.monitoring_framework.weekly_reviews.map((review, idx) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                              <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              {review}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Monthly Assessments */}
                    {recommendations.riskAssessment.monitoring_framework.monthly_assessments && (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h3 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Monthly Assessments
                        </h3>
                        <ul className="space-y-2">
                          {recommendations.riskAssessment.monitoring_framework.monthly_assessments.map((assessment, idx) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                              <Check className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                              {assessment}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Success Safeguards */}
              {recommendations.riskAssessment.success_safeguards && recommendations.riskAssessment.success_safeguards.length > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    Success Safeguards
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recommendations.riskAssessment.success_safeguards.map((safeguard, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                        <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{safeguard}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Risk Assessment Not Available</h3>
              <p className="text-sm text-gray-400">
                Risk assessment data wasn't generated for this analysis.
              </p>
            </div>
          )}
        </div>
      )}
                </div>

        {/* Saved Analyses Modal */}
        {showSavedAnalyses && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-500" />
                  <h2 className="text-lg font-bold text-white">Saved Analyses</h2>
                </div>
                <button 
                  onClick={() => setShowSavedAnalyses(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="p-4 border-b border-gray-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by business, category, or country..."
                    value={savedSearchTerm}
                    onChange={(e) => setSavedSearchTerm(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto max-h-[50vh]">
                {isLoadingSaved ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredSavedAnalyses.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No saved analyses found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredSavedAnalyses.map((analysis) => (
                      <div
                        key={analysis.id}
                        onClick={() => handleLoadSavedAnalysis(analysis)}
                        className="bg-gray-800 border border-gray-700 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold text-white mb-1">
                              {analysis.businessInput?.substring(0, 60)}...
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              <span>{analysis.selectedCategory}</span>
                              <span>•</span>
                              <span>{analysis.country}</span>
                              <span>•</span>
                              <span>{formatSavedDate(analysis.created_at)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {analysis.recommendations?.overallConfidence && (
                              <span className={`text-xs font-semibold ${getConfidenceColor(analysis.recommendations.overallConfidence)}`}>
                                {analysis.recommendations.overallConfidence}%
                              </span>
                            )}
                            <button
                              onClick={(e) => handleDeleteSavedAnalysis(analysis.id, e)}
                              disabled={deletingId === analysis.id}
                              className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              {deletingId === analysis.id ? (
                                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-400" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Preview of platforms */}
                        {analysis.recommendations?.platforms && (
                          <div className="flex items-center gap-2 mt-3">
                            {analysis.recommendations.platforms.slice(0, 4).map((platform, idx) => (
                              <span key={idx} className="text-sm" title={platform.platform_name}>
                                {getPlatformIcon(platform.platform_name)}
                              </span>
                            ))}
                            {analysis.recommendations.platforms.length > 4 && (
                              <span className="text-xs text-gray-500">
                                +{analysis.recommendations.platforms.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Implementation Plan Modal */}
        {showImplementationPlan && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getPlatformIcon(showImplementationPlan.platform_name)}</span>
                  <div>
                    <h2 className="text-lg font-bold text-white">{showImplementationPlan.platform_name} Implementation</h2>
                    <p className="text-sm text-gray-400">Step-by-step setup guide</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowImplementationPlan(null)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {showImplementationPlan.implementation_guide ? (
                  <div className="space-y-6">
                    {/* Setup Steps */}
                    {showImplementationPlan.implementation_guide.steps?.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-purple-400">{idx + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-white mb-1">{step.title}</h3>
                          <p className="text-sm text-gray-400">{step.description}</p>
                          {step.duration && (
                            <span className="text-xs text-purple-400 mt-2 inline-block">
                              ⏱️ {step.duration}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Best Practices */}
                    {showImplementationPlan.implementation_guide.best_practices && (
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-emerald-400 mb-3">Best Practices</h3>
                        <ul className="space-y-2">
                          {showImplementationPlan.implementation_guide.best_practices.map((practice, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                              <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              {practice}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Common Mistakes */}
                    {showImplementationPlan.implementation_guide.common_mistakes && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-red-400 mb-3">Common Mistakes to Avoid</h3>
                        <ul className="space-y-2">
                          {showImplementationPlan.implementation_guide.common_mistakes.map((mistake, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                              <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              {mistake}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-400">
                      Follow these general steps to set up your {showImplementationPlan.platform_name} advertising:
                    </p>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-purple-400">1</span>
                        Create or access your {showImplementationPlan.platform_name} Ads account
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-purple-400">2</span>
                        Set up conversion tracking and install the pixel/tag on your website
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-purple-400">3</span>
                        Define your target audience based on the recommendations above
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-purple-400">4</span>
                        Create your first campaign with {formatCurrency(showImplementationPlan.budget || 0)} monthly budget
                      </li>
                      <li className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-purple-400">5</span>
                        Monitor performance and optimize based on data
                      </li>
                    </ol>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => setShowImplementationPlan(null)}
                  className="w-full bg-purple-500 text-white py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors"
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Modal */}
        {showPreferencesModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <Sliders className="w-5 h-5 text-purple-500" />
                  <h2 className="text-lg font-bold text-white">Platform Preferences</h2>
                </div>
                <button 
                  onClick={() => setShowPreferencesModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Platforms Owned */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-white mb-2">Platforms You Already Use</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Select platforms where you already have active accounts. We'll prioritize these in recommendations.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'TikTok Ads', 'Twitter/X Ads', 'Pinterest Ads', 'YouTube Ads', 'Snapchat Ads', 'Microsoft Ads', 'Amazon Ads', 'Reddit Ads', 'Spotify Ads'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => {
                          if (platformsOwned.includes(platform)) {
                            setPlatformsOwned(platformsOwned.filter(p => p !== platform));
                          } else {
                            setPlatformsOwned([...platformsOwned, platform]);
                          }
                        }}
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                          platformsOwned.includes(platform)
                            ? 'bg-emerald-500/20 border border-emerald-500 text-emerald-300'
                            : 'bg-gray-800 border border-gray-700 text-gray-300 hover:border-gray-600'
                        }`}
                      >
                        <span>{getPlatformIcon(platform)}</span>
                        <span className="truncate">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platforms to Exclude */}
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">Platforms to Exclude</h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Select platforms you don't want in your recommendations (e.g., platforms you've tried and didn't work).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'TikTok Ads', 'Twitter/X Ads', 'Pinterest Ads', 'YouTube Ads', 'Snapchat Ads', 'Microsoft Ads', 'Amazon Ads', 'Reddit Ads', 'Spotify Ads'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => {
                          if (platformsToExclude.includes(platform)) {
                            setPlatformsToExclude(platformsToExclude.filter(p => p !== platform));
                          } else {
                            setPlatformsToExclude([...platformsToExclude, platform]);
                          }
                        }}
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                          platformsToExclude.includes(platform)
                            ? 'bg-red-500/20 border border-red-500 text-red-300'
                            : 'bg-gray-800 border border-gray-700 text-gray-300 hover:border-gray-600'
                        }`}
                      >
                        <span>{getPlatformIcon(platform)}</span>
                        <span className="truncate">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setShowPreferencesModal(false)}
                  className="flex-1 bg-gray-700 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUserPreferences}
                  className="flex-1 bg-purple-500 text-white py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Form View (Steps 1-3)
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700">
     
{/* Header */}
<div className="bg-gray-900/80 backdrop-blur-md border-b border-purple-500/20">
  <div className="w-full px-4 py-4">
    {/* Mobile Layout */}
    <div className="flex flex-col gap-4 sm:hidden">
      {/* Top row - Dashboard and action buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all font-medium"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span className="text-sm">Dashboard</span>
        </button>
        
        <div className="flex items-center gap-2">
          <button
            onClick={openSavedAnalyses}
            className="p-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all"
          >
            <FileText className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowPreferencesModal(true)}
            className="p-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all"
          >
            <Sliders className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Bottom row - Logo and title centered */}
      <div className="flex items-center justify-center gap-3">
        <div className="p-2 bg-purple-500/20 rounded-xl">
          <Brain className="w-6 h-6 text-purple-500" />
        </div>
        <div className="text-center">
          <h1 className="text-lg font-bold text-white">Platform Intelligence</h1>
          <p className="text-xs text-gray-400">AI-powered global strategy engine</p>
        </div>
      </div>
    </div>
    
    {/* Desktop Layout */}
    <div className="hidden sm:flex items-center justify-between max-w-7xl mx-auto">
      {/* Dashboard Button - Left */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all font-medium"
      >
        <ArrowRight className="w-4 h-4 rotate-180" />
        <span className="text-sm">Dashboard</span>
      </button>
      
      {/* Logo and Title - Center */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-500/20 rounded-xl">
          <Brain className="w-6 h-6 text-purple-500" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-white">Platform Intelligence</h1>
          <p className="text-xs text-gray-400">AI-powered global strategy engine</p>
        </div>
      </div>
      
      {/* Saved Analyses and Preferences - Right */}
      <div className="flex items-center gap-2">
        <button
          onClick={openSavedAnalyses}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all"
        >
          <FileText className="w-4 h-4" />
          <span className="text-sm">Saved Analyses</span>
        </button>
        <button
          onClick={() => setShowPreferencesModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-all"
        >
          <Sliders className="w-4 h-4" />
          <span className="text-sm">Preferences</span>
        </button>
      </div>
    </div>
  </div>
</div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                currentStep >= step 
                  ? 'bg-purple-500 border-purple-500 text-white' 
                  : 'border-gray-600 text-gray-500'
              }`}>
                {currentStep > step ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span>{step}</span>
                )}
              </div>
              {step < 3 && (
                <div className={`w-24 h-1 mx-2 rounded ${
                  currentStep > step ? 'bg-purple-500' : 'bg-gray-600'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

       {/* Step Labels */}
        <div className="flex justify-between mb-8 max-w-lg mx-auto px-2">
        <span className={`text-xs text-center ${currentStep >= 1 ? 'text-purple-400' : 'text-gray-500'}`}>Business<br className="sm:hidden" /> Info</span>
        <span className={`text-xs text-center ${currentStep >= 2 ? 'text-purple-400' : 'text-gray-500'}`}>Budget<br className="sm:hidden" /> & Goals</span>
        <span className={`text-xs text-center ${currentStep >= 3 ? 'text-purple-400' : 'text-gray-500'}`}>Target<br className="sm:hidden" /> Audience</span>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
            <div className="flex-1">
              <p className="text-sm text-red-500">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 text-lg ml-3">✕</button>
          </div>
        )}

        {/* Step 1: Business Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Describe Your Business
              </h2>
              <textarea
                value={businessInput}
                onChange={(e) => setBusinessInput(e.target.value)}
                placeholder="Example: We are a B2B SaaS company that provides project management software for creative agencies. Our target customers are marketing agencies with 10-50 employees. We charge $99-499/month per team."
                className="w-full h-32 bg-gray-800/50 border border-gray-600 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                Be specific about what you sell, who you sell to, and your price point for better recommendations.
              </p>
            </div>

            {/* Business Category */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-500" />
                Business Category
              </h2>
             <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
             {businessCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      selectedCategory === category.id
                        ? 'bg-purple-500/20 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-xl">{category.icon}</span>
                    <span className="text-sm">{category.name}</span>
                  </button>
                ))}
              </div>
              {selectedCategory === 'custom' && (
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Enter your business category..."
                  className="w-full mt-4 bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              )}
            </div>

            {/* Business Model */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Business Model
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3  gap-3">
               {businessModels.map((model) => (
                  <button
                    key={model.id}
                    onClick={() => setBusinessModel(model.id)}
                    className={`p-3 rounded-xl border transition-all text-left ${
                      businessModel === model.id
                        ? 'bg-purple-500/20 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-sm font-medium">{model.name}</span>
                    <p className="text-xs text-gray-400 mt-1">{model.description}</p>
                  </button>
                ))}
              </div>
              {businessModel === 'custom' && (
                <input
                  type="text"
                  value={customBusinessModel}
                  onChange={(e) => setCustomBusinessModel(e.target.value)}
                  placeholder="Describe your business model..."
                  className="w-full mt-4 bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              )}
            </div>

           {/* Country Selection */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-500" />
            Target Market
          </h2>
          
          {/* Toggle between dropdown and custom */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => {
                setUseCustomLocation(false);
                setCountry('United States');
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !useCustomLocation 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Select Country
            </button>
            <button
              onClick={() => {
                setUseCustomLocation(true);
                setCountry('');
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                useCustomLocation 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Custom Location
            </button>
          </div>
          
          {/* Country Dropdown */}
          {!useCustomLocation && (
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
            >
              {countriesData.length > 0 ? (
                countriesData.map((c, index) => (
                  <option key={`country-${c.code || index}`} value={c.name}>{c.name}</option>
                ))
              ) : (
                <option value="United States">United States</option>
              )}
            </select>
          )}
          
          {/* Custom Location Input */}
          {useCustomLocation && (
            <div>
              <input
                type="text"
                value={country}
                placeholder="e.g., California, USA or London, UK or Texas + Florida"
                className="w-full bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                onChange={(e) => setCountry(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter a state, city, region, or multiple locations separated by commas.
              </p>
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-3">
            Recommendations will be tailored to this market.
          </p>
        </div>
          </div>
        )}

        {/* Step 2: Budget & Goals */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Currency Selection */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Currency
              </h2>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
              >
                {currenciesData.length > 0 ? (
                  currenciesData.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))
                ) : (
                  Object.entries(CURRENCY_DATA).map(([code, data]) => (
                    <option key={code} value={code}>{code} - {data.name}</option>
                  ))
                )}
              </select>
            </div>

            {/* Monthly Budget */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Monthly Advertising Budget
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3  gap-3">
              {getBudgetRanges().map((budget) => (
                  <button
                    key={budget.id}
                    onClick={() => setMonthlyBudget(budget.id)}
                    className={`p-3 rounded-xl border transition-all text-left ${
                      monthlyBudget === budget.id
                        ? 'bg-purple-500/20 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-sm font-medium">{budget.range}</span>
                    <p className="text-xs text-gray-400 mt-1">{budget.label}</p>
                  </button>
                ))}
              </div>
              {monthlyBudget === 'custom' && (
                <div className="mt-4">
                  <label className="text-sm text-gray-400 mb-2 block">Enter your monthly budget in {currency}:</label>
                  <input
                    type="number"
                    value={customBudget}
                    onChange={(e) => setCustomBudget(e.target.value)}
                    placeholder={`e.g., 5000`}
                    className="w-full bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
              )}
            </div>

            {/* Primary Goal */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Primary Advertising Goal
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3  gap-3">
              {primaryGoals.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setPrimaryGoal(goal.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      primaryGoal === goal.id
                        ? 'bg-purple-500/20 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-xl">{goal.icon}</span>
                    <div className="text-left">
                      <span className="text-sm font-medium block">{goal.name}</span>
                      <span className="text-xs text-gray-400">{goal.description}</span>
                    </div>
                  </button>
                ))}
              </div>
              {primaryGoal === 'custom' && (
                <input
                  type="text"
                  value={customPrimaryGoal}
                  onChange={(e) => setCustomPrimaryGoal(e.target.value)}
                  placeholder="Describe your primary goal..."
                  className="w-full mt-4 bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              )}
            </div>

            {/* Experience Level */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Your Advertising Experience Level
              </h2>
              <p className="text-sm text-gray-400 mb-4">
                This helps us tailor the complexity of recommendations to match your skill level.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {experienceLevels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setExperienceLevel(level.id)}
                    className={`p-4 rounded-xl border transition-all text-left ${
                      experienceLevel === level.id
                        ? 'bg-purple-500/20 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{level.icon}</span>
                      <div>
                        <span className="text-sm font-semibold block">{level.name}</span>
                        <span className="text-xs text-gray-400">{level.description}</span>
                      </div>
                    </div>
                    {experienceLevel === level.id && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <ul className="space-y-1">
                          {level.features.map((feature, idx) => (
                            <li key={idx} className="text-xs text-gray-400 flex items-center gap-2">
                              <Check className="w-3 h-3 text-purple-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Target Audience */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Describe Your Target Audience
              </h2>
              <textarea
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="Example: Marketing managers and CMOs at mid-size companies (100-500 employees), aged 30-50, interested in productivity and team collaboration. They're tech-savvy, decision makers, and value efficiency and ROI."
                className="w-full h-40 bg-gray-800/50 border border-gray-600 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                Include demographics, interests, pain points, and behaviors for more accurate platform recommendations.
              </p>
            </div>

            {/* Quick Audience Prompts */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-white mb-4">Quick Prompts (Click to Add)</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'Decision makers',
                  'Budget holders',
                  'Small business owners',
                  'Enterprise clients',
                  'Tech-savvy',
                  'Price conscious',
                  'Early adopters',
                  'Quality focused',
                  'Millennials',
                  'Gen Z',
                  'Parents',
                  'Professionals'
                ].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => setTargetAudience(prev => prev ? `${prev}, ${prompt}` : prompt)}
                    className="px-3 py-1 text-xs bg-gray-700 text-gray-300 rounded-full hover:bg-purple-500/20 hover:text-purple-300 transition-all"
                  >
                    + {prompt}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary Before Analysis */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Ready for AI Analysis
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Category:</span>
                  <p className="text-white font-medium">
                    {selectedCategory === 'custom' 
                      ? customCategory 
                      : businessCategories.find(c => c.id === selectedCategory)?.name}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Business Model:</span>
                  <p className="text-white font-medium">
                    {businessModel === 'custom' 
                      ? customBusinessModel 
                      : businessModels.find(m => m.id === businessModel)?.name}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Market:</span>
                  <p className="text-white font-medium">{country}</p>
                </div>
                <div>
                  <span className="text-gray-400">Budget:</span>
                  <p className="text-white font-medium">
                    {monthlyBudget === 'custom' 
                      ? formatCurrency(parseFloat(customBudget) || 0) 
                      : getBudgetRanges().find(b => b.id === monthlyBudget)?.range} /mo
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Goal:</span>
                  <p className="text-white font-medium">
                    {primaryGoal === 'custom' 
                      ? customPrimaryGoal 
                      : primaryGoals.find(g => g.id === primaryGoal)?.name}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Experience:</span>
                  <p className="text-white font-medium">
                    {experienceLevels.find(l => l.id === experienceLevel)?.name}
                  </p>
                </div>
              </div>
              
              {/* Preferences Summary */}
              {(platformsOwned.length > 0 || platformsToExclude.length > 0) && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  {platformsOwned.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs text-gray-400">Platforms you own:</span>
                      {platformsOwned.map((p, idx) => (
                        <span key={idx} className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded">
                          {getPlatformIcon(p)} {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {platformsToExclude.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-400">Excluded:</span>
                      {platformsToExclude.map((p, idx) => (
                        <span key={idx} className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded">
                          {getPlatformIcon(p)} {p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 gap-4">
        <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 text-sm sm:text-base ${
              currentStep === 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back
          </button>

          <button
            onClick={nextStep}
            disabled={!canProceed()}
            className={`px-4 sm:px-8 py-3 rounded-xl font-medium transition-all flex items-center gap-2 text-sm sm:text-base ${
              canProceed()
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {currentStep === 3 ? (
              <>
                <Brain className="w-4 h-4" />
                Generate AI Recommendations
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Saved Analyses Modal (also shown in form view) */}
      {showSavedAnalyses && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-bold text-white">Saved Analyses</h2>
              </div>
              <button 
                onClick={() => setShowSavedAnalyses(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by business, category, or country..."
                  value={savedSearchTerm}
                  onChange={(e) => setSavedSearchTerm(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {isLoadingSaved ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredSavedAnalyses.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No saved analyses found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSavedAnalyses.map((analysis) => (
                    <div
                      key={analysis.id}
                      onClick={() => handleLoadSavedAnalysis(analysis)}
                      className="bg-gray-800 border border-gray-700 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-white mb-1">
                            {analysis.businessInput?.substring(0, 60)}...
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span>{analysis.selectedCategory}</span>
                            <span>•</span>
                            <span>{analysis.country}</span>
                            <span>•</span>
                            <span>{formatSavedDate(analysis.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {analysis.recommendations?.overallConfidence && (
                            <span className={`text-xs font-semibold ${getConfidenceColor(analysis.recommendations.overallConfidence)}`}>
                              {analysis.recommendations.overallConfidence}%
                            </span>
                          )}
                          <button
                            onClick={(e) => handleDeleteSavedAnalysis(analysis.id, e)}
                            disabled={deletingId === analysis.id}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            {deletingId === analysis.id ? (
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-red-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {analysis.recommendations?.platforms && (
                        <div className="flex items-center gap-2 mt-3">
                          {analysis.recommendations.platforms.slice(0, 4).map((platform, idx) => (
                            <span key={idx} className="text-sm" title={platform.platform_name}>
                              {getPlatformIcon(platform.platform_name)}
                            </span>
                          ))}
                          {analysis.recommendations.platforms.length > 4 && (
                            <span className="text-xs text-gray-500">
                              +{analysis.recommendations.platforms.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal (also shown in form view) */}
      {showPreferencesModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <Sliders className="w-5 h-5 text-purple-500" />
                <h2 className="text-lg font-bold text-white">Platform Preferences</h2>
              </div>
              <button 
                onClick={() => setShowPreferencesModal(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-white mb-2">Platforms You Already Use</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Select platforms where you already have active accounts. We'll prioritize these in recommendations.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3  gap-2">
                  {['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'TikTok Ads', 'Twitter/X Ads', 'Pinterest Ads', 'YouTube Ads', 'Snapchat Ads', 'Microsoft Ads', 'Amazon Ads', 'Reddit Ads', 'Spotify Ads'].map((platform) => (
                    <button
                      key={platform}
                      onClick={() => {
                        if (platformsOwned.includes(platform)) {
                          setPlatformsOwned(platformsOwned.filter(p => p !== platform));
                        } else {
                          setPlatformsOwned([...platformsOwned, platform]);
                        }
                      }}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                        platformsOwned.includes(platform)
                          ? 'bg-emerald-500/20 border border-emerald-500 text-emerald-300'
                          : 'bg-gray-800 border border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <span>{getPlatformIcon(platform)}</span>
                      <span className="truncate">{platform}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Platforms to Exclude</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Select platforms you don't want in your recommendations (e.g., platforms you've tried and didn't work).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {['Meta Ads', 'Google Ads', 'LinkedIn Ads', 'TikTok Ads', 'Twitter/X Ads', 'Pinterest Ads', 'YouTube Ads', 'Snapchat Ads', 'Microsoft Ads', 'Amazon Ads', 'Reddit Ads', 'Spotify Ads'].map((platform) => (
                    <button
                      key={platform}
                      onClick={() => {
                        if (platformsToExclude.includes(platform)) {
                          setPlatformsToExclude(platformsToExclude.filter(p => p !== platform));
                        } else {
                          setPlatformsToExclude([...platformsToExclude, platform]);
                        }
                      }}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                        platformsToExclude.includes(platform)
                          ? 'bg-red-500/20 border border-red-500 text-red-300'
                          : 'bg-gray-800 border border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <span>{getPlatformIcon(platform)}</span>
                      <span className="truncate">{platform}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowPreferencesModal(false)}
                className="flex-1 bg-gray-700 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveUserPreferences}
                className="flex-1 bg-purple-500 text-white py-2 rounded-lg font-medium hover:bg-purple-600 transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformIntelligence;